import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {
    Box, Divider,
    IconButton, Stack, Typography,
    Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow,
    Paper,
    Popover, Button
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import {MoneyField, UpDownIconDisplay, useSplitTabsTableContainerRef} from '@/components/common';
import type {AuditBuildCompanyFSTreeItems} from '@/services/auditService/auditCompanyFSCategoryService';
import type {CompanyAnnualSelectItem} from '@/services/accountService/companyAnnualService';
import {formatAsAtDate, formatAmountDisplay, parseAmountDisplay} from '@/utils/dataUtils';
import {hasChildren, toNestedTree} from '@/utils/fsDerived';
import {FS_CODE, isCode, findNodeByCodes, BS_HEADER_CODES} from '@/utils/fsCodes';
import {useFsCategoryStore} from '@/store/audit/fsCategoryStore';
import {useFsAdjustStore} from '@/store/audit/fsAdjustStore';
import {useCompanyAnnualStore} from '@/store/audit/companyAnnualStore';
import {
    resolveAdjustmentColumnHeaderYoa,
} from '@/utils/auditPeriodSplit';
import {isMustFillScheduleCode, ScheduleChip} from '@/pages/components/fs-report/ScheduleChip';
import {FsMaterialityHeader} from '@/pages/components/fs-report/FsMaterialityHeader';
import {
    FsStatementTableSkeletonRows,
    buildAdjustmentSkeletonColumns,
    buildFsBsSkeletonColumns,
} from '@/pages/components/fs-report/FsStatementTableSkeleton';
import {InlineEditableName, canEditItemAlias, isItemAliasLocked} from '@/pages/components/fs-report/InlineEditableName';
import {
    adjustColumnToggleHeaderSx,
    FS_LEVEL2_CELL_PY,
    fsAmountCellContentSx,
    fsCompactCellPadSx,
    fsCompactIconButtonSx,
    fsCompactMoneyFieldSx,
    fsCompactNameSx,
    lastYearColumnSeparatorSx,
} from '@/pages/components/fs-report/fsColumnSx';
import {appendFsChineseName, glExcelLineName, normalizeGlFsSummaryDisplayAmount, resolveFsChineseName} from '@/utils/glToFsMapper';
import {buildFsSummaryVisibleByFsId} from '@/utils/fsHideZeroRows';
import {
    resolveBsInventoriesFromBsTree,
    shouldHidePlCostOfSalesRow,
} from '@/utils/plCostOfSalesVisibility';
import {computePlAfterTaxAdjustmentsByRefNo, computePlAfterTaxAutoAdjustmentsByType} from '@/pages/components/fs-report/plAfterTaxAdjustBridge';
import {TAX_AUTO_ADJ_TYPE} from '@/pages/components/fs-report/plMultiTotalTaxAutoMerge';

// 转换函数
function convertAuditFsItemVOToFsSimpleData(vo: any): any {
    const level = vo.level || 0;

    // Recursively convert children if they exist
    const children = vo.children && Array.isArray(vo.children)
        ? vo.children.map(convertAuditFsItemVOToFsSimpleData)
        : [];

    return {
        fsId: vo.fsId,
        level: level,
        name: vo.name || '',
        nameEn: vo.nameEn ?? null,
        nameZh: vo.nameZh ?? null,
        aliasEn: vo.aliasEn ?? null,
        aliasZh: vo.aliasZh ?? null,
        aliasLocked: vo.aliasLocked ?? null,
        statement: vo.statement ?? null,
        code: vo.code || '',
        entry: (vo.entry || '') as 'Dr' | 'Cr',
        scheduleName: vo.scheduleName || '',
        scheduleCode: vo.scheduleCode || '',
        openingAmount: vo.openingAmount ?? vo.client ?? vo.amount ?? 0,
        adjustment: (Array.isArray(vo.adjustment) ? vo.adjustment : vo.adjustByPapers)?.map((paper: any) => ({
            refNo: paper.refNo || '',
            adjustment: paper.adjustment || 0,
            headerId: paper.headerId,
            adjustmentMode: paper.adjustmentMode,
            isAutoAdj: paper.isAutoAdj ?? (paper.adjustmentMode === 4),
            autoAdjType: paper.autoAdjType,
            yearOfAssessment: paper.yearOfAssessment ?? null,
        })) || [],
        finalAmount: vo.audited || 0,
        lastYearAudited: vo.lastYearAudited ?? 0,
        adjustByPaper: vo.adjustByPaper || false,
        wpStatus: vo.wpStatus ?? null,
        wpIssueRemark: vo.wpIssueRemark ?? null,
        outstanding: vo.outstanding ?? null,
        outstandingRemark: vo.outstandingRemark ?? null,
        notes: vo.notes ?? null,
        isCustom: vo.isCustom ?? false,
        customNodeId: vo.customNodeId ?? null,
        templateCodeRef: vo.templateCodeRef ?? null,
        children: children
    };
}

import {
    bsMakeLeafGetter,
    bsComputedMap,
    bsComputeComputedAmounts,
    bsResolveAdjustmentValue,
    bsNameFor,
    sumDescendants,
    computeDerivedRetainedEarnings,
    getRetainedProfitsStoredAmount,
    inferRetainedEarningsUseStored,
    plAfterTaxLastYearFromMap,
    resolveRetainedEarningsUseStored,
    resolveRetainedEarningsAutoSplit,
    hasRetainedEarningsComparativeLastYear,
    retainedEarningsLastYearComponentsFromMap,
    resolveRetainedEarningsRollForwardMismatch,
} from '@/pages/components/fs-report/FsFormula';
import {useAdjustmentStore} from '@/store/audit/adjustmentStore';
import {AdjustmentMode} from '@/services/auditService/adjustmentModes';
import {isExclusiveCode, EXCLUSIVE_CODES} from '@/adjustment/adjust/dialogs/utils/adjustment-helpers';
import {RoundingAdjustDialog} from '@/pages/components/fs-report/RoundingAdjustDialog';
import type {RoundingPeriodSlice, RoundingTargetNode} from '@/pages/components/fs-report/RoundingAdjustDialog';
import {
    buildRoundingAdjustmentMaps,
    buildRoundingSaveTargets,
    emptyRoundingAdjustGroup,
    hasRoundingTargets,
    notifyRoundingSaved,
    notifyRoundingDialogCancelled,
    prepareRoundingTargetNodes,
    registerRoundingGateController,
    requestAuditedRoundingGate,
    resetLatestRoundingDifference,
    retryActiveRoundingGate,
    setLatestRoundingDifference,
    unregisterRoundingGateController,
    type RoundingApplyEntry,
} from '@/pages/components/fs-report/roundingAdjustHelpers';
import {roundingAdjustService} from '@/services/auditService/roundingAdjustService';
import {useQueryClient} from '@tanstack/react-query';
import {useQueryRoundingAdjust} from '@/services/auditService/adjustmentQueries';
import {useFillRemainingViewport} from '@/hooks/useFillRemainingViewport';

// Protected header codes (do not edit as leaf; act as section headers)
const HEADER_CODE_LIST = BS_HEADER_CODES;

function normalizeLineCode(input: any): string {
    const raw = String(input || '').trim();
    if (!raw) return '';
    const upper = raw.toUpperCase();
    if (upper.startsWith('BS-') || upper.startsWith('PL-') || upper.startsWith('FS-')) return raw;
    const mapped = useFsAdjustStore.getState().getLineByCode(raw)?.code;
    const mappedUpper = String(mapped || '').trim().toUpperCase();
    if (mappedUpper.startsWith('BS-') || mappedUpper.startsWith('PL-') || mappedUpper.startsWith('FS-')) return mapped as string;
    return raw;
}

function getScheduleName(map: Map<string, string>, item: any): string {
    const normalized = normalizeLineCode(item?.code || item?.scheduleCode);
    const legacy = String(item?.scheduleCode || '').trim();
    return map.get(normalized) || (legacy ? map.get(legacy) : undefined) || (item?.scheduleName || '');
}

// Helper component to display change percentage with icon


export type BalanceSheetMode = 'fs' | 'adjustment';

export interface BalanceSheetHandle {
    getLines: () => Array<{ fsId: number; amount: number }>
}

export interface BalanceSheetProps {
    mode: BalanceSheetMode;
    annualId: number;
    // FS-only
    onErrorChange?: (err: string | null) => void;
    onStatusChange?: (s: {
        netAssets: number;
        capitalReserves: number;
        equal: boolean;
        zero: boolean;
        lastYearNetAssets?: number;
        lastYearCapitalReserves?: number;
        equalLastYear?: boolean;
        zeroLastYear?: boolean;
    }) => void;
    // Adjustment-only
    onScheduleClick?: (item: any) => void;
    // FS Opening Edit Mode
    fsOpeningEditMode?: boolean;
    onFsOpeningDataChange?: (rowData: Array<{
        fsId: number;
        code: string;
        scheduleCode?: string;
        scheduleName: string;
        statement: 'BS' | 'PL';
        entry?: string;
        adjustmentAmount: number;
        openingAmount: number;
        closingAmount: number;
        adjustHisAmount: number;
    }>) => void;
    // Hide Last Year and Change columns
    hideLastYearColumns?: boolean;
    /** 只显示 Last Year，隐藏 Current 与 Change（Create GL 上年录入） */
    lastYearOnly?: boolean;
    /** Last Year AI 合并说明：fsCode → remark 文案 */
    lastYearRemarkByFsCode?: Record<string, string> | null;
    // Callback when clicking on adjustment cell (headerId, refNo, item)
    onAdjustmentCellClick?: (headerId: string, refNo: string, item: any) => void;
    // Shared Adjust Total mode state (controlled from parent for synchronization)
    adjustTotalMode?: boolean;
    onAdjustTotalModeChange?: (mode: boolean) => void;
    /** Report adjust column count (manual + Auto) so parent can pick initial Adjust Total vs detail */
    onAdjustColumnCountChange?: (count: number) => void;
    // 只读模式：所有节点不可编辑，仅做渲染显示
    readOnly?: boolean;
    /** GL FS Summary：点击科目名跳转 Tiny Mode（传 fsCode） */
    onFsLineClick?: (fsCode: string) => void;
    /** 可跳转的科目码；未传则凡有 code 的叶子均可点 */
    navigableFsCodes?: Set<string>;
    /** GL FS Summary：fsCode → Excel 导入列名，与 Tiny Mode 一致 */
    glExcelNameByFsCode?: Record<string, string> | null;
    /** GL FS Summary：fsCode → Excel 导入中文列名 */
    glExcelNameZhByFsCode?: Record<string, string> | null;
    /** GL FS Summary：开=客户别名，关=标准科目名（与 Tiny Mode 同源） */
    showClientAlias?: boolean;
    // Shared Audited Rounded mode state (controlled from parent for synchronization)
    auditedRounded?: AuditedMode;
    onAuditedRoundedChange?: (mode: AuditedMode) => void;
    /** Adjustment-only: roll-forward opening mismatch warning for parent SplitTabsPanel */
    onRollForwardWarningChange?: (msg: string | null) => void;
    /** Adjustment-only: allow double-click rename of unlocked leaf item names */
    itemAliasEditEnabled?: boolean;
    onItemAliasChange?: (payload: {
        fsId: number;
        code: string;
        statement: 'BS' | 'PL';
        displayName: string;
    }) => Promise<void>;
    /** Notify parent when internal table init loading flips (FsDetail progressive shell). */
    onLoadingChange?: (loading: boolean) => void;
}

export type AuditedMode = 'normal' | 'round' | 'rounded';

export const BalanceSheet = forwardRef<BalanceSheetHandle, BalanceSheetProps>(({
                                                                                   mode,
                                                                                   annualId,
                                                                                   onErrorChange,
                                                                                   onStatusChange,
                                                                                   onScheduleClick,
                                                                                   fsOpeningEditMode,
                                                                                   onFsOpeningDataChange,
                                                                                   hideLastYearColumns = false,
                                                                                   lastYearOnly = false,
                                                                                   lastYearRemarkByFsCode = null,
                                                                                   onAdjustmentCellClick,
                                                                                   adjustTotalMode: controlledAdjustTotalMode,
                                                                                   onAdjustTotalModeChange,
                                                                                   onAdjustColumnCountChange,
                                                                                   readOnly = false,
                                                                                   onFsLineClick,
                                                                                   navigableFsCodes,
                                                                                   glExcelNameByFsCode = null,
                                                                                   glExcelNameZhByFsCode = null,
                                                                                   showClientAlias = false,
                                                                                   auditedRounded: controlledAuditedRounded,
                                                                                   onAuditedRoundedChange,
                                                                                   onRollForwardWarningChange,
                                                                                   itemAliasEditEnabled = false,
                                                                                   onItemAliasChange,
                                                                                   onLoadingChange,
                                                                               }, ref) => {
    // 根据 mode 选择数据源
    // adjustment 模式: 使用 fsAdjustStore (展示层 - queryAdjustmentList 数据)
    // fs 模式: 使用 fsCategoryStore (编辑层 - 用户编辑数据)

    // 正确订阅 store 的数据变化
    const bsTreeFromStore = useFsAdjustStore(state => state.adjustRecord?.bsLine);
    const fsAdjustStore = useFsAdjustStore();
    const fsCategoryStore = useFsCategoryStore();

    // 获取当前调整模式 - 必须在顶层调用
    const adjustmentStore = useAdjustmentStore();
    const currentMode = adjustmentStore.currentMode;

    // Mode 2 (FS_OPENING) 辅助输入功能状态
    const mode2CapturedValue = adjustmentStore.mode2CapturedValue;
    const mode2CapturedFsId = adjustmentStore.mode2CapturedFsId;
    const mode2CapturedEntry = adjustmentStore.mode2CapturedEntry;
    const mode2AssistUsed = adjustmentStore.mode2AssistUsed;
    const setMode2CapturedValue = adjustmentStore.setMode2CapturedValue;
    const setMode2AssistUsed = adjustmentStore.setMode2AssistUsed;
    const fsOpeningResetTrigger = adjustmentStore.fsOpeningResetTrigger;
    const bsRetainedEarningsUseStored = useFsCategoryStore(s => s.bsRetainedEarningsUseStored);

    const plValuesByYoa = useFsCategoryStore(s => s.plValuesByYoa);
    /** FsDetail MULTI: ≥2 YOA columns in PL amounts map */
    const isFsMultiYoa = useMemo(() => {
        if (mode !== 'fs') return false;
        const keys = Object.keys(plValuesByYoa || {}).filter(
            (k) => k && Object.keys((plValuesByYoa as any)[k] || {}).length > 0,
        );
        return keys.length >= 2;
    }, [mode, plValuesByYoa]);

    // 根据 mode 获取数据
    const {
        companyId: sCompanyId,
        companyName: sCompanyName,
        bsTree: sBSTreeFromCategory,
        bsValues: sBSValues,
        plAfterTax: sPLAfterTax,
        plRoundedAfterTax: sPLRoundedAfterTax,
        plPeriodType: sPLPeriodType,
        afterTaxClientFromPL: sAfterTaxClientFromPL,
        adjustments: sAdjustments,
        allowedScheduleCodes: sAllowedScheduleCodes,
        scheduleNameMap: sScheduleNameMap,
        setBsValues,
        setBsRetainedEarningsUseStored,
    } = fsCategoryStore;

    // adjustment 模式从 fsAdjustStore 获取树，fs 模式从 fsCategoryStore 获取
    const sBSTreeFromAdjust = useMemo(() => {
        if (mode !== 'adjustment') return null;
        if (!bsTreeFromStore) return null;
        // 直接转换 store 中的数据
        const tree = bsTreeFromStore.map(convertAuditFsItemVOToFsSimpleData);
        return tree;
    }, [mode, bsTreeFromStore]); // 当 bsTreeFromStore 变化时重新计算
    const sBSTree = mode === 'adjustment' ? sBSTreeFromAdjust : sBSTreeFromCategory;

    const resolvedCompanyName = sCompanyName ?? null;
    const resolvedInitialTree = useMemo(() => sBSTree ?? [], [sBSTree]);
    const resolvedExternalValues = useMemo(() => sBSValues, [sBSValues]);
    const resolvedOnExternalChange = setBsValues;
    const resolvedAfterTaxFromPL = sPLAfterTax;
    const resolvedAfterTaxLastYearFromPL = fsCategoryStore.plAfterTaxLastYear;
    const glSuppressPlAfterTaxLastYear = useFsCategoryStore(s => s.glSuppressPlAfterTaxLastYear);
    const categoryLoadedCompanyId = useFsCategoryStore(s => s.loadedCompanyId);
    const categoryLoadedAnnualId = useFsCategoryStore(s => s.loadedAnnualId);
    const adjustRecordCompanyId = useFsAdjustStore(s => Number(s.adjustRecord?.companyId || 0) || 0);
    const adjustRecordAnnualId = useFsAdjustStore(s => Number(s.adjustRecord?.annualId || 0) || 0);
    const resolvedCompanyId = (Number(sCompanyId) || adjustRecordCompanyId || undefined) || undefined;
    // Account GL / 其他公司 FS 会话残留：loadedCompanyId 指向别的公司
    const hasForeignCategorySession = mode === 'adjustment'
        && adjustRecordCompanyId > 0
        && adjustRecordAnnualId > 0
        && categoryLoadedCompanyId != null
        && (
            Number(categoryLoadedCompanyId) !== adjustRecordCompanyId
            || Number(categoryLoadedAnnualId || 0) !== adjustRecordAnnualId
        );
    // B6.1 / FS Summary：AL 上年在 mapper 已拆成 (−GL!BU10 − LFP)，LFP 上年正常展示。
    // 仅当显式 glSuppress（旧会话）时才压 LFP。
    const effectivePlLastYearFromPL = (
        !hasForeignCategorySession && glSuppressPlAfterTaxLastYear
    )
        ? 0
        : (resolvedAfterTaxLastYearFromPL ?? 0);
    const resolvedPeriodTypeFromPL = sPLPeriodType;
    const resolvedAfterTaxClientFromPL = sAfterTaxClientFromPL ?? null;
    const resolvedAdjustments = sAdjustments ?? null;
    const resolvedAllowedScheduleCodes = sAllowedScheduleCodes;
    const resolvedScheduleNameMapFromParent = sScheduleNameMap;

    const [fsTree, setFsTree] = useState<AuditBuildCompanyFSTreeItems[]>([]);
    const [asAtPeriodTo, setAsAtPeriodTo] = useState<string | null>(null);
    const [presentationCurrency, setPresentationCurrency] = useState<string | null>(null);
    const [yearOfAssessment, setYearOfAssessment] = useState<string | null>(null);
    const [lastYearOfAssessment, setLastYearOfAssessment] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        onLoadingChange?.(loading);
    }, [loading, onLoadingChange]);

    // FS mode local state
    const [amountDisplays, setAmountDisplays] = useState<Record<number, string>>({});
    const [lastYearAmounts, setLastYearAmounts] = useState<Record<number, string>>({});
    const [fsError, setFsError] = useState<string | null>(null);
    const prevInitialTreeRef = useRef<any[] | null>(null);
    /** Suppress suffix blur restoring old value when BS-EQ-AL split mode is cancelled */
    const retainedProfitsCancelRef = useRef(false);
    /** Cap root to remaining viewport; only TableContainer scrolls. */
    const fillRootRef = useRef<HTMLDivElement>(null);
    const fillHeightPx = useFillRemainingViewport(fillRootRef);
    /** Report table H-overflow to SplitTabsPanel for icon vs full-name tabs. */
    const tableContainerRef = useSplitTabsTableContainerRef<HTMLDivElement>();

    // 共享的 expanded 状态（FS 和 adjustment 模式都使用）
    const [expanded, setExpanded] = useState<Record<number, boolean>>({});

    // ADJ mode local state
    const [fsOpeningAmounts, setFsOpeningAmounts] = useState<Record<number, number>>({});

    // Adjust Total mode: when true, merge all adjustment columns into one total column
    // Use controlled props if provided, otherwise use internal state
    const [internalAdjustTotalMode, setInternalAdjustTotalMode] = useState<boolean>(false);
    const adjustTotalMode = controlledAdjustTotalMode !== undefined ? controlledAdjustTotalMode : internalAdjustTotalMode;
    const setAdjustTotalMode = onAdjustTotalModeChange || setInternalAdjustTotalMode;

    // ===== Rounding Adjustment 状态 (仅 adjustment 模式) =====
    const queryClient = useQueryClient();
    // React Query 缓存 rounding 数据（staleTime 5min，组件重挂载不重复请求）
    const {data: roundingGroup, isLoading: isRoundingLoading} = useQueryRoundingAdjust(
        Number(annualId),
        mode === 'adjustment' && !!annualId
    );
    const {
        roundingAdjustments,
        roundingSourceStatement,
        roundingSavedNodeValues,
    } = useMemo(() => {
        const maps = buildRoundingAdjustmentMaps(roundingGroup);
        return {
            roundingAdjustments: maps.bsDisplayMap,
            roundingSourceStatement: maps.sourceMap,
            roundingSavedNodeValues: maps.savedNodeValues,
        };
    }, [roundingGroup]);
    // 弹窗开关
    const [roundingDialogOpen, setRoundingDialogOpen] = useState(false);
    /** 用户主动 Cancel 后，本轮 round 模式下不再自动弹窗，直至显式 requestAuditedRoundingGate */
    const roundingDialogDismissedRef = useRef(false);
    // 当前检测到的差值
    const [roundingDifference, setRoundingDifference] = useState(0);
    // 弹窗可选节点列表
    const [roundingTargetNodes, setRoundingTargetNodes] = useState<RoundingTargetNode[]>([]);
    // 共享 Popover 删除确认状态（表格层级唯一实例，避免 per-cell Popover DOM）
    const [deleteAnchor, setDeleteAnchor] = useState<{ el: HTMLElement; nodeCode: string } | null>(null);
    // PL 树订阅（通过 hook 而非 getState）
    const plTreeFromStore = useFsAdjustStore(state => state.adjustRecord?.plLine);
    const adjustSpanTypeForRounding = useFsAdjustStore(state => state.adjustRecord?.spanType);
    const adjustPeriodsForRounding = useFsAdjustStore(state => state.adjustRecord?.periods);
    const adjustEngagementYoa = useFsAdjustStore(state => state.adjustRecord?.yearOfAssessment);
    /** FsAdjustment MULTI（≥2 periods）— mirror FsDetail MULTI AL Client rules */
    const isMultiAdjustmentPl = mode === 'adjustment'
        && adjustSpanTypeForRounding === 'MULTI'
        && Array.isArray(adjustPeriodsForRounding)
        && adjustPeriodsForRounding.length >= 2;

    /** MULTI·PL Period Tabs（供 Rounding Dialog PL 子 Tab） */
    const roundingPlPeriods: RoundingPeriodSlice[] | null = useMemo(() => {
        if (adjustSpanTypeForRounding !== 'MULTI') return null;
        if (!Array.isArray(adjustPeriodsForRounding) || adjustPeriodsForRounding.length < 2) return null;
        const slices = adjustPeriodsForRounding.map((p, idx) => ({
            key: p.key || `period-${idx}`,
            yearOfAssessment: String(p.yearOfAssessment || '').trim(),
            label: p.label || p.yearOfAssessment || undefined,
        })).filter(p => !!p.yearOfAssessment);
        return slices.length >= 2 ? slices : null;
    }, [adjustSpanTypeForRounding, adjustPeriodsForRounding]);

    const sPLTreeFromAdjust = useMemo(() => {
        if (mode !== 'adjustment') return null;
        if (!plTreeFromStore) return null;
        return plTreeFromStore.map(convertAuditFsItemVOToFsSimpleData);
    }, [mode, plTreeFromStore]);

    // 监听 fsOpeningResetTrigger 变化，清空 fsOpeningAmounts
    useEffect(() => {
        if (fsOpeningResetTrigger > 0) {
            setFsOpeningAmounts({});
        }
    }, [fsOpeningResetTrigger]);

    // 当 fsOpeningEditMode 激活时，展开包含 EXCLUSIVE_CODES 的父节点
    useEffect(() => {
        if (!fsOpeningEditMode || !fsTree || fsTree.length === 0) return;

        const bsExclusiveCodes = Array.from(EXCLUSIVE_CODES).filter(code => code.startsWith('BS-'));
        const parentIdsToExpand: Set<number> = new Set();

        // 递归查找包含 EXCLUSIVE_CODES 的节点及其所有父节点
        const findParentsOfExclusiveCodes = (nodes: any[], parentIds: number[] = []): void => {
            for (const node of nodes || []) {
                const nodeCode = String(node?.code || '').trim().toUpperCase();
                const currentParentIds = [...parentIds];

                // 如果当前节点是 EXCLUSIVE_CODE，标记所有父节点需要展开
                if (bsExclusiveCodes.includes(nodeCode)) {
                    currentParentIds.forEach(id => parentIdsToExpand.add(id));
                }

                // 如果有子节点，继续递归
                if (Array.isArray(node?.children) && node.children.length > 0) {
                    findParentsOfExclusiveCodes(node.children, [...currentParentIds, Number(node.fsId)]);
                }
            }
        };

        findParentsOfExclusiveCodes(fsTree as any[]);

        // 更新 expanded 状态，展开所有需要展开的父节点
        if (parentIdsToExpand.size > 0) {
            setExpanded(prev => {
                const next = {...prev};
                parentIdsToExpand.forEach(id => {
                    next[id] = true;
                });
                return next;
            });
        }
    }, [fsOpeningEditMode, fsTree]);

    const scheduleNameMap = useMemo(() => {
        if (resolvedScheduleNameMapFromParent) return resolvedScheduleNameMapFromParent;
        const map = new Map<string, string>();
        for (const hdr of (resolvedAdjustments || [])) {
            for (const ln of (hdr?.lines || [])) {
                const legacyScheduleCode = String((ln as any)?.scheduleCode || '').trim();
                const code = normalizeLineCode((ln as any)?.code || (ln as any)?.scheduleCode);
                const name = String((ln as any)?.scheduleName || '').trim();
                if (code && name && !map.has(code)) map.set(code, name);
                if (legacyScheduleCode && name && !map.has(legacyScheduleCode)) map.set(legacyScheduleCode, name);
            }
        }
        return map;
    }, [resolvedAdjustments, resolvedScheduleNameMapFromParent]);

    // Build nested tree from initialTree - optimized with early return
    useEffect(() => {
        // Skip if no data
        if (!resolvedInitialTree || resolvedInitialTree.length === 0) {
            setFsTree([]);
            setAmountDisplays({});
            setLastYearAmounts({});
            setExpanded({});
            setLoading(false);
            return;
        }

        const tree = toNestedTree((resolvedInitialTree || []) as any[]);
        setFsTree(tree as any);
        const treeChanged = prevInitialTreeRef.current !== resolvedInitialTree;
        if (treeChanged) {
            prevInitialTreeRef.current = resolvedInitialTree;
        }
        if (mode === 'fs') {
            // Restore BS-EQ-AL stored flag when re-entering FS from Modify
            if (treeChanged && (tree as any[]).length > 0) {
                let useStored = inferRetainedEarningsUseStored(
                    tree as any[],
                    fsCategoryStore.plTree,
                    resolvedExternalValues || {},
                    fsCategoryStore.bsLastYearValues || {},
                    fsCategoryStore.plLastYearValues || {},
                );
                // MULTI + no AL/LFP/DIV comparative LY + derived≈0 → skip auto-Split on stale opening
                if (isFsMultiYoa && useStored) {
                    const plLy = hideLastYearColumns
                        ? 0
                        : plAfterTaxLastYearFromMap(
                            fsCategoryStore.plTree,
                            fsCategoryStore.plLastYearValues || {},
                        );
                    const bsLyMap = fsCategoryStore.bsLastYearValues || {};
                    const derivedAl = hideLastYearColumns
                        ? 0
                        : computeDerivedRetainedEarnings(tree as any[], {
                            bsLastYearMap: bsLyMap,
                            plAfterTaxLastYear: plLy,
                        });
                    const lyParts = hideLastYearColumns
                        ? {alLastYear: 0, divLastYear: 0, plAfterTaxLastYear: 0}
                        : retainedEarningsLastYearComponentsFromMap(
                            tree as any[],
                            bsLyMap,
                            plLy,
                        );
                    const hasComparativeLy = hasRetainedEarningsComparativeLastYear(lyParts);
                    if (!resolveRetainedEarningsAutoSplit(
                        getRetainedProfitsStoredAmount(tree as any[], resolvedExternalValues || {}),
                        derivedAl,
                        hasComparativeLy,
                    )) {
                        useStored = false;
                    }
                }
                setBsRetainedEarningsUseStored(useStored);
            }
            // Init amount displays
            let nextAmountDisplays: Record<number, string> = {};
            if (resolvedExternalValues && Object.keys(resolvedExternalValues).length > 0) {
                for (const [k, v] of Object.entries(resolvedExternalValues)) {
                    nextAmountDisplays[Number(k)] = formatAmountDisplay(v as any);
                }
            } else {
                const visit = (nodes: any[]) => {
                    for (const item of nodes || []) {
                        const leaf = !Array.isArray(item?.children) || item.children.length === 0;
                        const isDerived = (
                            isCode(item, ...FS_CODE.BS.NET_CURRENT) ||
                            isCode(item, ...FS_CODE.BS.NET_ASSETS) ||
                            isCode(item, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)
                        );
                        const isHeader = isCode(item, ...HEADER_CODE_LIST);
                        if (leaf && !isDerived && !isHeader) {
                            const rawAmt = item.openingAmount ?? item.amount ?? item.client ?? 0;
                            nextAmountDisplays[item.fsId] = formatAmountDisplay(rawAmt);
                        }
                        if (Array.isArray(item.children) && item.children.length > 0) visit(item.children);
                    }
                };
                visit(tree as any);
            }
            setAmountDisplays(nextAmountDisplays);

            // Init lastYear amounts from store
            const bsLastYearValues = fsCategoryStore.bsLastYearValues;
            if (bsLastYearValues && Object.keys(bsLastYearValues).length > 0) {
                const formatted: Record<number, string> = {};
                for (const [k, v] of Object.entries(bsLastYearValues)) {
                    formatted[Number(k)] = formatAmountDisplay(v as any);
                }
                setLastYearAmounts(formatted);
            }

            // Always expand all parents in FS mode when tree (re)loads so Excel-import
            // create flow does not render a single collapsed leaf.
            const expandedNodes: Record<number, boolean> = {};
            const markExpanded = (nodes: any[]) => {
                for (const node of nodes || []) {
                    const hasKids = Array.isArray(node?.children) && node.children.length > 0;
                    if (hasKids) {
                        expandedNodes[Number(node.fsId)] = true;
                        markExpanded(node.children);
                    }
                }
            };
            markExpanded(tree as any);
            setExpanded(expandedNodes);
        }
        setLoading(false);
    }, [
        resolvedInitialTree,
        resolvedExternalValues,
        mode,
        setBsRetainedEarningsUseStored,
        isFsMultiYoa,
        hideLastYearColumns,
    ]);

    const updateFsImportedAmount = useCallback((fsId: number, num: number, item?: any) => {
        let nextNum = num;
        if (item && isCode(item, ...FS_CODE.BS.DIVIDEND) && nextNum > 0) {
            nextNum = -nextNum;
        }
        setAmountDisplays(prev => {
            const next = {...prev, [fsId]: String(nextNum)};
            resolvedOnExternalChange?.(next);
            return next;
        });
    }, [resolvedOnExternalChange]);

    /** Enter split (EditIcon): keep override active even when value is 0; seed from derived when empty. */
    const handleRetainedProfitsEditStart = useCallback((
        fsId: number,
        item: any,
        derived: number | null,
    ) => {
        retainedProfitsCancelRef.current = false;
        setBsRetainedEarningsUseStored(true);
        const current = parseAmountDisplay(amountDisplays[fsId] ?? '0');
        // Seed from calculated when no meaningful persisted override (null/0), matching DIR Final split.
        if (Math.abs(current) <= 0.001 && derived != null) {
            updateFsImportedAmount(fsId, derived, item);
        }
    }, [amountDisplays, updateFsImportedAmount, setBsRetainedEarningsUseStored]);

    /** Split suffix edit: 0 is a valid override while split is active. */
    const handleRetainedProfitsSuffixChange = useCallback((
        fsId: number,
        num: number,
        item: any,
    ) => {
        // Split MoneyField fires suffix blur on unmount after Cancel — ignore stale values.
        if (retainedProfitsCancelRef.current) return;
        setBsRetainedEarningsUseStored(true);
        updateFsImportedAmount(fsId, num, item);
    }, [updateFsImportedAmount, setBsRetainedEarningsUseStored]);

    /** Cancel split: clear override to 0 (persist via store) and roll parents up from derived. */
    const handleRetainedProfitsEditCancel = useCallback((
        fsId: number,
        item: any,
    ) => {
        retainedProfitsCancelRef.current = true;
        setBsRetainedEarningsUseStored(false);
        updateFsImportedAmount(fsId, 0, item);
        setTimeout(() => {
            retainedProfitsCancelRef.current = false;
        }, 100);
    }, [updateFsImportedAmount, setBsRetainedEarningsUseStored]);

    // Load periodTo, presentationCurrency, yearOfAssessment via companyAnnualStore
    useEffect(() => {
        let mounted = true;
        const run = async () => {
            try {
                const store = useCompanyAnnualStore.getState();
                const info = await store.getYearOfAssessment(Number(annualId));
                if (mounted) {
                    setAsAtPeriodTo(info?.periodTo || null);
                    setYearOfAssessment(info?.yearOfAssessment || null);
                }
                if (mounted) setLastYearOfAssessment(info?.lastYearOfAssessment || null);
                const currency = await store.getPresentationCurrency(Number(annualId));
                if (mounted) setPresentationCurrency(currency || null);
            } catch {
            }
        };
        run();
        return () => {
            mounted = false;
        };
    }, [annualId]);

    /**
     * Display-only: MULTI adjustment Client / Adjust# / Audited caption YOA span.
     * Does not touch yearOfAssessment state (amounts / rounding / WP unchanged).
     * Last Year caption stays lastYearOfAssessment (original).
     */
    const columnHeaderYoa = useMemo(() => {
        if (mode !== 'adjustment') return yearOfAssessment;
        if (String(adjustSpanTypeForRounding || '').toUpperCase() !== 'MULTI') return yearOfAssessment;
        const periods = Array.isArray(adjustPeriodsForRounding) ? adjustPeriodsForRounding : [];
        if (periods.length < 2) return yearOfAssessment;
        return resolveAdjustmentColumnHeaderYoa({
            spanType: 'MULTI',
            periods,
            engagementYoa: adjustEngagementYoa,
            fallbackYoa: yearOfAssessment,
        });
    }, [
        mode,
        adjustSpanTypeForRounding,
        adjustPeriodsForRounding,
        adjustEngagementYoa,
        yearOfAssessment,
    ]);

    // FS mode: central leaf getter and computed map - memoized with stable dependencies
    const leafGetterFS = useMemo(() => {
        if (mode !== 'fs') return () => 0;
        return bsMakeLeafGetter('input', {
            amountDisplays,
            afterTaxFromPL: resolvedAfterTaxFromPL ?? 0,
            headerCodes: HEADER_CODE_LIST,
        });
    }, [mode, amountDisplays, resolvedAfterTaxFromPL]);

    const leafGetterLastYearFS = useMemo(() => {
        if (mode !== 'fs') return () => 0;
        return bsMakeLeafGetter('input', {
            amountDisplays: lastYearAmounts,
            afterTaxFromPL: effectivePlLastYearFromPL,
            headerCodes: HEADER_CODE_LIST,
        });
    }, [mode, lastYearAmounts, effectivePlLastYearFromPL]);

    // Calculate Derived Retained Earnings for FS (Current Year)
    // Current AL Client = Last Year(BS-EQ-AL) + Last Year(BS-EQ-LFP) + Last Year(BS-EQ-DIV)
    // MULTI + no Last Year columns (firstYearAudit): Client AL = 0 (no comparative)
    // SINGLE / MULTI with Last Year columns: original roll-forward unchanged
    const derivedRetainedEarningsFS = useMemo(() => {
        if (mode !== 'fs') return null;
        if (isFsMultiYoa && hideLastYearColumns) return 0;
        return computeDerivedRetainedEarnings(fsTree as any[], {
            bsLastYearMap: lastYearAmounts,
            plAfterTaxLastYear: effectivePlLastYearFromPL,
        });
    }, [mode, fsTree, lastYearAmounts, effectivePlLastYearFromPL, isFsMultiYoa, hideLastYearColumns]);

    // Last Year Cap/Net must use lastYearAmounts for AL — never reverse-derive from Current
    // client/override (editing Current must not move Last Year balance).
    const computeAllLastYearAmounts = useMemo(() => {
        if (mode !== 'fs' || !fsTree || fsTree.length === 0) return {} as Record<number, number>;
        return bsComputedMap((fsTree || []) as any, 'input', {
            amountDisplays: lastYearAmounts,
            afterTaxFromPL: effectivePlLastYearFromPL,
            headerCodes: HEADER_CODE_LIST,
        });
    }, [mode, fsTree, lastYearAmounts, effectivePlLastYearFromPL]);

    const computeAllComputedAmounts = useMemo(() => {
        if (mode !== 'fs' || !fsTree || fsTree.length === 0) return {} as Record<number, number>;
        // Mirror DIR Final split: while override mode is on, parent rollup uses stored (incl. 0).
        return bsComputedMap((fsTree || []) as any, 'input', {
            amountDisplays,
            afterTaxFromPL: resolvedAfterTaxFromPL ?? 0,
            headerCodes: HEADER_CODE_LIST,
            derivedRetainedEarnings: bsRetainedEarningsUseStored ? undefined : derivedRetainedEarningsFS,
        });
    }, [mode, fsTree, amountDisplays, resolvedAfterTaxFromPL, derivedRetainedEarningsFS, bsRetainedEarningsUseStored]);

    // Imbalance detection (FS mode)
    useEffect(() => {
        if (mode !== 'fs') return;
        const NET_ASSETS = findNodeByCodes(fsTree as any[], FS_CODE.BS.NET_ASSETS);
        const CAP_RES = findNodeByCodes(fsTree as any[], FS_CODE.BS.CAPITAL_AND_RESERVES);
        const net = NET_ASSETS ? (computeAllComputedAmounts[NET_ASSETS.fsId] ?? 0) : 0;
        // Calculate cap: use computeAllComputedAmounts to get BS-EQ total (includes derivedRetainedEarnings)
        const cap = CAP_RES ? (computeAllComputedAmounts[CAP_RES.fsId] ?? 0) : 0;
        const diff = Math.round((net - cap) * 100) / 100;

        let lastNet = 0;
        let lastCap = 0;
        let lastDiff = 0;
        if (!hideLastYearColumns) {
            lastNet = NET_ASSETS ? (computeAllLastYearAmounts[NET_ASSETS.fsId] ?? 0) : 0;
            lastCap = CAP_RES ? (computeAllLastYearAmounts[CAP_RES.fsId] ?? 0) : 0;
            lastDiff = Math.round((lastNet - lastCap) * 100) / 100;
        }

        const fmtNum = (v: number) => v.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
        const messages: string[] = [];
        // lastYearOnly：只提示上年；否则照常提示本年
        if (!lastYearOnly) {
            if (diff > 0) {
                messages.push(`This Year: Unbalance of ${fmtNum(diff)}, need a Cr. entry to balance.`);
            } else if (diff < 0) {
                messages.push(`This Year: Unbalance of ${fmtNum(diff)}, need a Dr. entry to balance.`);
            }
        }

        if (!hideLastYearColumns) {
            const lyPrefix = lastYearOnly ? 'Last Year' : 'Last year';
            if (lastDiff > 0) {
                messages.push(`${lyPrefix}: Unbalance of ${fmtNum(lastDiff)}, need a Cr. entry to balance.`);
            } else if (lastDiff < 0) {
                messages.push(`${lyPrefix}: Unbalance of ${fmtNum(lastDiff)}, need a Dr. entry to balance.`);
            }
        }

        if (typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG_FS_BALANCE') === '1') {
            const invNode = findNodeByCodes(fsTree as any[], FS_CODE.BS.INVENTORIES);
            const lfpNode = findNodeByCodes(fsTree as any[], FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD);
            const invId = invNode?.fsId != null ? Number(invNode.fsId) : undefined;
            const lfpId = lfpNode?.fsId != null ? Number(lfpNode.fsId) : undefined;
            // eslint-disable-next-line no-console
            console.log('[FS_BALANCE][BS]', {
                netAssets: net,
                capitalReserves: cap,
                diff,
                lastNet,
                lastCap,
                lastDiff,
                afterTaxFromPL: resolvedAfterTaxFromPL,
                inventories: invId != null ? {
                    fsId: invId,
                    input: amountDisplays[invId],
                    computed: computeAllComputedAmounts[invId],
                    lastYearInput: lastYearAmounts[invId],
                    lastYearComputed: computeAllLastYearAmounts[invId],
                } : null,
                profitForYear: lfpId != null ? {
                    fsId: lfpId,
                    computed: computeAllComputedAmounts[lfpId],
                    lastYearComputed: computeAllLastYearAmounts[lfpId],
                } : null,
                messages,
            });
        }

        const nextError = messages.length > 0 ? messages.join('\n') : null;
        setFsError((prev) => (prev === nextError ? prev : nextError));
        try {
            onStatusChange?.({
                netAssets: net,
                capitalReserves: cap,
                equal: Math.abs(diff) === 0,
                zero: net === 0 && cap === 0,
                ...(hideLastYearColumns ? {} : {
                    lastYearNetAssets: lastNet,
                    lastYearCapitalReserves: lastCap,
                    equalLastYear: Math.abs(lastDiff) === 0,
                    zeroLastYear: lastNet === 0 && lastCap === 0,
                })
            });
        } catch {
        }
        // amountDisplays / lastYearAmounts / resolvedAfterTaxFromPL only used for DEBUG logs;
        // computeAllComputedAmounts already tracks the values that affect balance.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, fsTree, computeAllComputedAmounts, computeAllLastYearAmounts, hideLastYearColumns, lastYearOnly, onStatusChange]);

    useEffect(() => {
        if (mode === 'fs') onErrorChange?.(fsError);
    }, [fsError, mode, onErrorChange]);

    // Sync lastYearAmounts to store for ProfitLoss to access
    useEffect(() => {
        if (mode !== 'fs') return;
        const store = useFsCategoryStore.getState();
        // 首帧本地还是空表时，不要把已经灌入的上年数冲掉
        if (
            Object.keys(lastYearAmounts).length === 0
            && Object.keys(store.bsLastYearValues || {}).length > 0
        ) {
            return;
        }
        const prev = store.bsLastYearValues || {};
        const next = lastYearAmounts || {};
        const prevKeys = Object.keys(prev);
        const nextKeys = Object.keys(next);
        if (
            prevKeys.length === nextKeys.length
            && nextKeys.every((k) => prev[Number(k)] === next[Number(k)])
        ) {
            return;
        }
        store.setBsLastYearValues(next);
    }, [mode, lastYearAmounts]);

    const glFsSummaryDisplayAmount = useCallback((
        fsCode: string | null | undefined,
        amount: number,
        parentFsCode?: string | null,
    ) => {
        if (!readOnly) return amount;
        return normalizeGlFsSummaryDisplayAmount(fsCode, amount, parentFsCode);
    }, [readOnly]);

    // ===== Rounding Adjustment: 页面加载回显已由 useQueryRoundingAdjust + useMemo 处理 =====

    // Name presentation based on derived or sign
    const getDisplayName = useCallback((item: AuditBuildCompanyFSTreeItems): string => {
        let amount = computeAllComputedAmounts[item.fsId] ?? 0;
        // For BS-EQ-AL, it is now derived in FS mode, so we want to use the computed amount, not leaf amount
        const isDerivedRetained = isCode(item, ...FS_CODE.BS.RETAINED_PROFITS_OR_LOSSES);

        if (!isDerivedRetained && !(item.fsId in computeAllComputedAmounts)) {
            amount = parseAmountDisplay(amountDisplays[item.fsId] || '0');
        }
        if (readOnly) {
            amount = glFsSummaryDisplayAmount(item.code, amount);
        }

        const name = bsNameFor(item, amount, {
            leafAmountGetter: isDerivedRetained ? undefined : (n) => {
                const raw = parseAmountDisplay(amountDisplays[n.fsId] || '0');
                return readOnly ? glFsSummaryDisplayAmount(n.code, raw) : raw;
            },
            afterTaxFromPL: resolvedAfterTaxFromPL ?? 0,
            periodType: (resolvedPeriodTypeFromPL as any) || null,
        });
        const fallback = showClientAlias
            ? ((item.aliasEn || item.name || item.nameEn || item.aliasZh || item.nameZh || (item.code as any) || '') as string)
            : ((item.name || item.nameEn || item.aliasEn || item.nameZh || item.aliasZh || (item.code as any) || '') as string);
        const english = name
            ?? glExcelLineName(item.code, glExcelNameByFsCode)
            ?? fallback;
        if (glExcelNameByFsCode == null) return english;
        if (showClientAlias) {
            return appendFsChineseName(
                english,
                glExcelLineName(item.code, glExcelNameZhByFsCode)
                    ?? resolveFsChineseName(item, null),
            );
        }
        return appendFsChineseName(english, resolveFsChineseName(item, glExcelNameZhByFsCode));
    }, [computeAllComputedAmounts, amountDisplays, resolvedAfterTaxFromPL, resolvedPeriodTypeFromPL, readOnly, glFsSummaryDisplayAmount, glExcelNameByFsCode, glExcelNameZhByFsCode, showClientAlias]);

    // 共享的 flatten 函数（FS 和 adjustment 模式都使用）
    // 返回包含 parentFsId 的结构，用于 Collapse 动画分组
    const flatten = useCallback((nodes: any[], level = 0, parentFsId: number | null = null): Array<{
        item: any;
        level: number;
        isParent: boolean;
        isSubtotal?: boolean;
        parentItem?: any;
        parentFsId: number | null;
        isFirstChild?: boolean;
        isLastChild?: boolean;
        childrenCount?: number;
    }> => {
        const out: Array<{
            item: any;
            level: number;
            isParent: boolean;
            isSubtotal?: boolean;
            parentItem?: any;
            parentFsId: number | null;
            isFirstChild?: boolean;
            isLastChild?: boolean;
            childrenCount?: number
        }> = [];
        for (let i = 0; i < (nodes || []).length; i++) {
            const n = nodes[i];
            const isParent = hasChildren(n);
            const isExpanded = !!expanded[Number(n.fsId)];
            const nodeLevel = (n as any).level;
            const childrenArr = n.children as any[] || [];

            out.push({
                item: n,
                level,
                isParent,
                parentFsId,
                isFirstChild: i === 0,
                isLastChild: i === nodes.length - 1,
                childrenCount: isParent ? childrenArr.length : 0
            });

            if (isParent) {
                // Always include children structure for Collapse animation
                const childRows = flatten(childrenArr, level + 1, Number(n.fsId));

                // Add subtotal if needed
                if (nodeLevel === 2 && isExpanded) {
                    childRows.push({
                        item: {
                            ...n,
                            fsId: `${n.fsId}_subtotal`,
                            name: '',
                        },
                        level: level + 1,
                        isParent: false,
                        isSubtotal: true,
                        parentItem: n,
                        parentFsId: Number(n.fsId),
                        isLastChild: true
                    });
                }

                // Only include children if expanded (Collapse handles animation)
                if (isExpanded) {
                    out.push(...childRows);
                }
            }
        }
        return out;
    }, [expanded]);

    // 共享的 toggleNode 函数
    const toggleNode = useCallback((id: number) => {
        setExpanded(prev => ({...prev, [id]: !prev[id]}));
    }, []);

    useImperativeHandle(ref, () => ({
        getLines: () => {
            if (mode !== 'fs') return [];
            const lines: Array<{ fsId: number; amount: number }> = [];
            const collectLeaves = (nodes: any[]) => {
                for (const it of nodes || []) {
                    const isParent = hasChildren(it);
                    const isDerived = isCode(it, ...FS_CODE.BS.NET_CURRENT) || isCode(it, ...FS_CODE.BS.NET_ASSETS) || isCode(it, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD);
                    const isRetainedProfits = isCode(it, ...FS_CODE.BS.RETAINED_PROFITS_OR_LOSSES);
                    const isProtectedHeader = isCode(it, ...HEADER_CODE_LIST);
                    if (!isParent && !isDerived && !isProtectedHeader) {
                        // BS-EQ-AL: persist override only while split/useStored is active (0 is valid);
                        // Cancel / normal mode persists 0 so next load uses derived roll-forward.
                        const amount = isRetainedProfits && !bsRetainedEarningsUseStored
                            ? 0
                            : parseAmountDisplay(amountDisplays[it.fsId] || '');
                        lines.push({fsId: it.fsId, amount});
                    }
                    if (isParent && it.children?.length) collectLeaves(it.children);
                }
            };
            collectLeaves(fsTree as any[]);
            return lines;
        }
    }), [mode, fsTree, amountDisplays, bsRetainedEarningsUseStored]);

    if (mode === 'adjustment') {
        // Adjustment mode rendering with TreeFsTable
        const subtreeIdsMap = useMemo(() => {
            const map = new Map<number, Set<number>>();
            const collect = (node: any): Set<number> => {
                const ownId = Number(node?.fsId);
                const childSets: Set<number>[] = (node?.children || []).map((c: any) => collect(c));
                const agg = new Set<number>([ownId]);
                childSets.forEach((s) => s.forEach((v) => agg.add(v)));
                map.set(ownId, agg);
                return agg;
            };
            for (const root of (fsTree || []) as any[]) collect(root);
            return map;
        }, [fsTree]);

        // Compute Last Year Audited values using the same approach as Client/Audited columns
        // Create a custom leaf getter that reads lastYearAudited field from each node
        // Moved up before computedClientMap/computedAuditedMap to support derived calculations
        const computedLastYearAuditedMap = useMemo(() => {
            const lastYearLeafGetter = (node: AuditBuildCompanyFSTreeItems): number => {
                // For header nodes, return 0
                if (isCode(node, ...HEADER_CODE_LIST)) return 0;
                // For PL link node (BS-EQ-LFP), use PL after-tax last year value directly
                // This ensures it's included in parent BS-EQ sum calculation
                if (isCode(node, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) {
                    return effectivePlLastYearFromPL;
                }
                // For regular leaf nodes, use lastYearAudited field from the node itself
                return Number((node as any)?.lastYearAudited ?? 0) || 0;
            };

            // Use bsComputeComputedAmounts to compute all parent sums and derived nodes
            // This will automatically handle NET_CURRENT, NET_ASSETS based on lastYearAudited values
            // Now BS-EQ-LFP's value is correctly included when computing parent sums
            const computed = bsComputeComputedAmounts((fsTree || []) as any, lastYearLeafGetter);

            // Ensure BS-EQ-LFP leaf value is in the map
            // bsComputeComputedAmounts only stores parent sums and derived nodes, not leaf LFP
            const LFP = findNodeByCodes((fsTree || []) as any[], FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD);
            if (LFP && !hasChildren(LFP)) {
                computed[Number(LFP.fsId)] = lastYearLeafGetter(LFP);
            }

            return computed;
        }, [fsTree, effectivePlLastYearFromPL]);

        // Calculate Derived Retained Earnings for Adjustment Mode
        // Same as FS: LY AL + LY LFP + LY DIV; MULTI + no Last Year cols → 0
        const derivedRetainedEarningsAdj = useMemo(() => {
            if (mode !== 'adjustment') return null;
            if (isMultiAdjustmentPl && hideLastYearColumns) return 0;
            const AL = findNodeByCodes(fsTree as any[], FS_CODE.BS.RETAINED_PROFITS_OR_LOSSES);
            const DIV = findNodeByCodes(fsTree as any[], FS_CODE.BS.DIVIDEND);

            // Local getter for Last Year Audited values (matches logic in computedLastYearAuditedMap but safe for direct use)
            const getter = (node: any) => {
                if (isCode(node, ...HEADER_CODE_LIST)) return 0;
                if (isCode(node, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) return effectivePlLastYearFromPL;
                return Number(node?.lastYearAudited ?? 0) || 0;
            };

            // Formula: Last Year AL + Last Year LFP + Last Year DIV
            const val_AL_LY = AL ? sumDescendants(AL as any, getter) : 0;
            const val_LFP_LY = effectivePlLastYearFromPL;
            const val_DIV_LY = DIV ? sumDescendants(DIV as any, getter) : 0;

            return val_AL_LY + val_LFP_LY + val_DIV_LY;
        }, [mode, fsTree, effectivePlLastYearFromPL, isMultiAdjustmentPl, hideLastYearColumns]);

        // BS-EQ-AL Client (adjustment) — mirrors FS Edit/Cancel:
        // 1.x Edit (override active, incl. explicit 0) → Client = user override
        // 2.x Cancel / backend null|0 (no override) → Client = roll-forward derived
        // Prefer fsCategoryStore.bsValues only when它属于当前公司年度（避免 GL/FS Summary 跨公司残留）
        const retainedProfitsStoredOpeningAdj = useMemo(() => {
            const AL = findNodeByCodes((fsTree || []) as any[], FS_CODE.BS.RETAINED_PROFITS_OR_LOSSES);
            if (!AL) return 0;
            const fsId = Number(AL.fsId);
            const fromTree = getRetainedProfitsStoredAmount((fsTree || []) as any[], undefined);
            if (
                !hasForeignCategorySession
                && bsRetainedEarningsUseStored
                && sBSValues
                && sBSValues[fsId] != null
                && sBSValues[fsId] !== ''
            ) {
                const fromStore = parseAmountDisplay(sBSValues[fsId]);
                // GL/FS Summary 会话残留：store 为 0 但 adj 树已有 submit 后的 AL → 以树为准
                if (Math.abs(fromStore) <= 0.001 && Math.abs(fromTree) > 0.001) {
                    return fromTree;
                }
                return fromStore;
            }
            return fromTree;
        }, [fsTree, bsRetainedEarningsUseStored, sBSValues, hasForeignCategorySession]);
        // MULTI adj: comparative = AL/LFP/DIV LY only (ignore other BS LY e.g. INV)
        const hasComparativeLastYearAdj = useMemo(() => {
            if (hideLastYearColumns) return false;
            const AL = findNodeByCodes((fsTree || []) as any[], FS_CODE.BS.RETAINED_PROFITS_OR_LOSSES);
            const DIV = findNodeByCodes((fsTree || []) as any[], FS_CODE.BS.DIVIDEND);
            const lyGetter = (node: any) => {
                if (isCode(node, ...HEADER_CODE_LIST)) return 0;
                if (isCode(node, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) {
                    return effectivePlLastYearFromPL;
                }
                return Number(node?.lastYearAudited ?? 0) || 0;
            };
            return hasRetainedEarningsComparativeLastYear({
                plAfterTaxLastYear: effectivePlLastYearFromPL,
                alLastYear: AL ? sumDescendants(AL as any, lyGetter) : 0,
                divLastYear: DIV ? sumDescendants(DIV as any, lyGetter) : 0,
            });
        }, [fsTree, effectivePlLastYearFromPL, hideLastYearColumns]);

        const hasPersistedRetainedProfitsOverrideAdj = useMemo(() => {
            if (derivedRetainedEarningsAdj == null) return false;
            // MULTI: no AL/LFP/DIV comparative LY + derived≈0 → do not auto-Split on stale opening.
            // Explicit bsRetainedEarningsUseStored / Cap Edit still OR'd below.
            if (isMultiAdjustmentPl) {
                return resolveRetainedEarningsAutoSplit(
                    retainedProfitsStoredOpeningAdj,
                    derivedRetainedEarningsAdj,
                    hasComparativeLastYearAdj,
                );
            }
            return resolveRetainedEarningsUseStored(
                retainedProfitsStoredOpeningAdj,
                derivedRetainedEarningsAdj,
            );
        }, [
            derivedRetainedEarningsAdj,
            retainedProfitsStoredOpeningAdj,
            isMultiAdjustmentPl,
            hasComparativeLastYearAdj,
        ]);
        // Original: Cap Edit flag OR persisted override (SINGLE / MULTI with LY cols unchanged)
        const useRetainedProfitsOverrideAdj = (
            !hasForeignCategorySession && bsRetainedEarningsUseStored
        ) || hasPersistedRetainedProfitsOverrideAdj;
        const effectiveDerivedRetainedEarningsAdj = useMemo(() => {
            if (derivedRetainedEarningsAdj == null) return null;
            // override → user data; otherwise → calculated
            return useRetainedProfitsOverrideAdj
                ? retainedProfitsStoredOpeningAdj
                : derivedRetainedEarningsAdj;
        }, [
            derivedRetainedEarningsAdj,
            useRetainedProfitsOverrideAdj,
            retainedProfitsStoredOpeningAdj,
        ]);

        // Sync flag when adj tree has a persisted non-zero override (reload / enter adj without FS session).
        useEffect(() => {
            if (!hasPersistedRetainedProfitsOverrideAdj) return;
            if (!bsRetainedEarningsUseStored) {
                setBsRetainedEarningsUseStored(true);
            }
        }, [
            hasPersistedRetainedProfitsOverrideAdj,
            bsRetainedEarningsUseStored,
            setBsRetainedEarningsUseStored,
        ]);

        // Use FsDetail formulas for both columns:
        // Client column uses 'amount' and PL client after-tax; Audited column uses 'finalAmount' and PL audited after-tax
        const computedClientMap = useMemo(() => (
            bsComputedMap((fsTree || []) as any, 'client', {
                afterTaxFromPL: resolvedAfterTaxFromPL ?? 0,
                afterTaxClientFromPL: resolvedAfterTaxClientFromPL ?? 0,
                headerCodes: HEADER_CODE_LIST,
                derivedRetainedEarnings: effectiveDerivedRetainedEarningsAdj
            })
        ), [fsTree, resolvedAfterTaxFromPL, resolvedAfterTaxClientFromPL, effectiveDerivedRetainedEarningsAdj]);

        const computedAuditedMap = useMemo(() => (
            bsComputedMap((fsTree || []) as any, 'audited', {
                afterTaxFromPL: resolvedAfterTaxFromPL ?? 0,
                afterTaxClientFromPL: resolvedAfterTaxClientFromPL ?? 0,
                headerCodes: HEADER_CODE_LIST,
                // We do NOT override audited map with derivedRetainedEarnings because Audited column
                // should technically be Client + Adjustments.
                // However, computedAuditedMap is generally not used for main display in adjustment mode
                // (main display uses clientVal + sumAdjustments).
                // If we wanted computedAuditedMap to be accurate for other uses, we'd need to know
                // if finalAmount includes the correct derived base.
            })
        ), [fsTree, resolvedAfterTaxFromPL, resolvedAfterTaxClientFromPL]);

        // BS-CL-OFL / BS-NCL-OFL 共享 Vehicle Loan WP；NCL 节点 openingAmount 常为 0，chip 可见性需参照 CL
        const vehicleLoanCurrentLine = useMemo(
            () => findNodeByCodes((fsTree || []) as any[], FS_CODE.BS.VEHICLE_LOAN_CURRENT),
            [fsTree],
        );
        const vehicleLoanChipHasTrackableValue = useCallback((node: any | null | undefined): boolean => {
            if (!node) return false;
            return Number(node?.openingAmount ?? 0) !== 0 || node?.wpStatus != null;
        }, []);

        // Grouped extra columns removed (draft, review, history)

        const isAutoAdjEntry = useCallback((adj: any): boolean => {
            return adj?.isAutoAdj === true || adj?.adjustmentMode === AdjustmentMode.ADJUST_AUTO_GEN;
        }, []);

        type AdjustmentColumnDef = {
            key: string;
            headerId?: string;
            /** MULTI + TAX only：多年 TAX header 合并渲染时的 id 列表 */
            mergeHeaderIds?: string[];
            refNo: string;
            isAutoAdj: boolean;
            autoAdjType?: string;
            title: string;
        };

        /** MULTI 仅 TAX 做多年合并求和；DIR/LOAN/Disposal/Exchange/TAX_EXCHANGE 按后端单条原样渲染 */
        const isMultiYearTaxAutoMerge = useCallback((autoAdjType?: string): boolean => {
            return String(autoAdjType || '').trim() === TAX_AUTO_ADJ_TYPE;
        }, []);

        const findAdjustmentEntry = useCallback((adjustments: any[] | undefined, col: AdjustmentColumnDef) => {
            if (!Array.isArray(adjustments)) return undefined;
            // WHY: 仅 TAX 多年 header 在渲染层求和；其它 auto 禁止叠加（对齐后端单条口径）
            if (col.isAutoAdj
                && isMultiYearTaxAutoMerge(col.autoAdjType)
                && Array.isArray(col.mergeHeaderIds)
                && col.mergeHeaderIds.length > 0) {
                const idSet = new Set(col.mergeHeaderIds.map(String));
                let sum = 0;
                let first: any;
                for (const a of adjustments) {
                    if (a?.headerId == null || !idSet.has(String(a.headerId))) continue;
                    if (!isAutoAdjEntry(a)) continue;
                    sum += Number(a?.adjustment ?? 0);
                    if (!first) first = a;
                }
                if (!first) return undefined;
                return {...first, adjustment: sum};
            }
            // headerId is UUID string (post adjustment-key migration); never coerce with Number()
            if (col.headerId != null && col.headerId !== '') {
                const headerId = String(col.headerId);
                return adjustments.find((a: any) => a?.headerId != null && String(a.headerId) === headerId);
            }
            return adjustments.find((a: any) =>
                String(a?.refNo) === col.refNo && isAutoAdjEntry(a) === col.isAutoAdj,
            );
        }, [isAutoAdjEntry, isMultiYearTaxAutoMerge]);

        const AUTO_ADJ_TYPE_ORDER = ['DIR_DTD', 'LOAN_BANK', 'LOAN_VEHICLE', 'GL_DISPOSAL', 'GL_EXCHANGE', 'TAX', 'TAX_EXCHANGE'];
        const adjustSpanType = useFsAdjustStore(s => s.adjustRecord?.spanType);
        // 仅 spanType=MULTI 才进入多年 Auto 列逻辑；避免 SINGLE 误入
        const isMultiYearAdjust = adjustSpanType === 'MULTI';

        // 列定义：从 queryAdjustmentList 返回的树节点 adjustment 数组收集（旧 /adjustment/list 已下线）
        // MULTI：仅 TAX 同 type 多年 header 合并为一列（单元格侧求和）；其余 auto 按 header 原样一列
        const adjustmentColumnDefs = useMemo((): AdjustmentColumnDef[] => {
            if (mode !== 'adjustment') return [];

            const dedupeKeyOf = (col: Pick<AdjustmentColumnDef, 'headerId' | 'refNo' | 'isAutoAdj' | 'autoAdjType' | 'mergeHeaderIds'>) => {
                if (col.isAutoAdj && isMultiYearAdjust && isMultiYearTaxAutoMerge(col.autoAdjType)) {
                    return `auto:${col.autoAdjType || col.refNo}`;
                }
                return col.headerId != null
                    ? `h:${col.headerId}`
                    : `r:${col.refNo}:${col.isAutoAdj ? 'auto' : 'manual'}`;
            };

            const seen = new Set<string>();
            const defs: AdjustmentColumnDef[] = [];
            const taxAutoFirst = new Map<string, AdjustmentColumnDef>();

            const collect = (nodes: any[]) => {
                for (const node of nodes || []) {
                    if (Array.isArray(node.adjustment)) {
                        for (const adj of node.adjustment) {
                            const refNo = String(adj?.refNo || '').trim();
                            if (!refNo) continue;
                            const headerId = adj?.headerId != null ? String(adj.headerId) : undefined;
                            const isAutoAdj = isAutoAdjEntry(adj);
                            const autoAdjType = adj?.autoAdjType ? String(adj.autoAdjType) : undefined;

                            if (isAutoAdj && isMultiYearAdjust && isMultiYearTaxAutoMerge(autoAdjType)) {
                                const typeKey = autoAdjType || refNo;
                                const existing = taxAutoFirst.get(typeKey);
                                if (existing) {
                                    if (headerId && !(existing.mergeHeaderIds || []).includes(headerId)) {
                                        existing.mergeHeaderIds = [...(existing.mergeHeaderIds || []), headerId];
                                    }
                                    continue;
                                }
                                const col: AdjustmentColumnDef = {
                                    key: `auto_adjust_${typeKey}`,
                                    headerId,
                                    mergeHeaderIds: headerId ? [headerId] : [],
                                    refNo,
                                    isAutoAdj: true,
                                    autoAdjType,
                                    title: `Adjust#${refNo}(Auto)`,
                                };
                                taxAutoFirst.set(typeKey, col);
                                const dedupeKey = dedupeKeyOf(col);
                                if (!seen.has(dedupeKey)) {
                                    seen.add(dedupeKey);
                                    defs.push(col);
                                }
                                continue;
                            }

                            const col: AdjustmentColumnDef = {
                                key: headerId != null ? `adjust_h_${headerId}` : `adjust_${refNo}_${isAutoAdj ? 'auto' : 'manual'}`,
                                headerId,
                                refNo,
                                isAutoAdj,
                                autoAdjType,
                                title: isAutoAdj ? `Adjust#${refNo}(Auto)` : `Adjust#${refNo}`,
                            };
                            const dedupeKey = dedupeKeyOf(col);
                            if (seen.has(dedupeKey)) continue;
                            seen.add(dedupeKey);
                            defs.push(col);
                        }
                    }
                    if (Array.isArray(node.children)) collect(node.children);
                }
            };
            collect(fsTree as any[]);
            collect((sPLTreeFromAdjust || []) as any[]);
            // MULTI: also scan each period plLine so PL-only Adjust# appear as empty columns on BS
            if (isMultiYearAdjust && Array.isArray(adjustPeriodsForRounding)) {
                for (const period of adjustPeriodsForRounding) {
                    const pl = period?.plLine;
                    if (Array.isArray(pl) && pl.length > 0) {
                        collect(pl as any[]);
                    }
                }
            }

            if (isMultiYearAdjust) {
                defs.sort((a, b) => {
                    if (a.isAutoAdj !== b.isAutoAdj) return a.isAutoAdj ? 1 : -1;
                    if (a.isAutoAdj && b.isAutoAdj) {
                        const ia = AUTO_ADJ_TYPE_ORDER.indexOf(a.autoAdjType || '');
                        const ib = AUTO_ADJ_TYPE_ORDER.indexOf(b.autoAdjType || '');
                        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
                    }
                    const na = parseFloat(a.refNo) || 0;
                    const nb = parseFloat(b.refNo) || 0;
                    if (na !== nb) return na - nb;
                    return String(a.refNo).localeCompare(String(b.refNo));
                });
            }
            return defs;
        }, [mode, fsTree, sPLTreeFromAdjust, isAutoAdjEntry, isMultiYearAdjust, isMultiYearTaxAutoMerge, adjustPeriodsForRounding]);

        // Adjust Total 折叠门槛：
        // - 原逻辑：手动 Adjust#N > 1 可合并
        // - 扩展：至少 1 条手动，且手动 + Adjust#N(Auto) 合计 ≥ 2 也可合并（不影响纯 Auto / 单手动场景）
        // - 再扩展：纯 Auto 且列数 ≥ 5 也可合并（与 FsAdjustment 初始化规则对齐）
        const manualAdjustmentColumnCount = useMemo(
            () => adjustmentColumnDefs.filter(col => !col.isAutoAdj).length,
            [adjustmentColumnDefs],
        );
        const autoAdjustmentColumnCount = adjustmentColumnDefs.length - manualAdjustmentColumnCount;
        const adjustColumnCount = adjustmentColumnDefs.length;
        const canCollapseAdjustColumns =
            adjustColumnCount >= 5
            || manualAdjustmentColumnCount > 1
            || (manualAdjustmentColumnCount >= 1 && autoAdjustmentColumnCount >= 1);

        // Notify parent of column count (manual + Auto) before paint — init Adjust Total vs detail
        useLayoutEffect(() => {
            if (mode !== 'adjustment') return;
            onAdjustColumnCountChange?.(adjustColumnCount);
        }, [mode, adjustColumnCount, onAdjustColumnCountChange]);

        // PL AFTER_TAX Adjust#N：优先从 PL 树本地计算（与 ProfitLoss 公式一致），
        // 避免依赖 ProfitLoss useEffect 写入 store（未挂载 / 时序会导致 BS-EQ-LFP 为空）。
        // Store 仅作回退（兼容仍依赖 setPlAdjustmentsByRefNo 的路径）。
        const plAdjustmentsByRefNoFromStore = useFsCategoryStore(s => s.plAdjustmentsByRefNo);
        const plCotInputMode = useFsCategoryStore(s => s.plCotInputMode ?? 'COS');
        const plBridgeOpts = useMemo(() => {
            const plTree = (sPLTreeFromAdjust || []) as any[];
            const bsInventories = resolveBsInventoriesFromBsTree((fsTree || []) as any[]);
            const hideCos = shouldHidePlCostOfSalesRow(bsInventories, plTree);
            return {
                plTree,
                isAutoAdjEntry,
                cotInputMode: plCotInputMode as 'COS' | 'PUR',
                showCosRowInAdjustment: !hideCos || plCotInputMode === 'PUR',
            };
        }, [sPLTreeFromAdjust, fsTree, isAutoAdjEntry, plCotInputMode]);

        const plAdjustmentsByRefNo = useMemo(() => {
            const refNos = adjustmentColumnDefs
                .filter((col) => !col.isAutoAdj)
                .map((col) => String(col.refNo || '').trim())
                .filter(Boolean);
            if (!plBridgeOpts.plTree.length || refNos.length === 0) {
                return plAdjustmentsByRefNoFromStore || {};
            }
            const local = computePlAfterTaxAdjustmentsByRefNo({
                ...plBridgeOpts,
                refNos: Array.from(new Set(refNos)),
            });
            return Object.keys(local).length > 0 ? local : (plAdjustmentsByRefNoFromStore || {});
        }, [plBridgeOpts, adjustmentColumnDefs, plAdjustmentsByRefNoFromStore]);

        // Tax Auto 等：PL-PAT 派生 Auto 列金额 → BS-EQ-LFP（与 ProfitLoss AFTER_TAX Auto 列一致）
        const plAutoAdjustmentsByType = useMemo(() => {
            const autoItems = adjustmentColumnDefs
                .filter((col) => col.isAutoAdj)
                .map((col) => ({
                    type: String(col.autoAdjType || col.refNo || '').trim(),
                    refNo: String(col.refNo || '').trim(),
                }))
                .filter((it) => !!it.type || !!it.refNo);
            if (!plBridgeOpts.plTree.length || autoItems.length === 0) return {};
            return computePlAfterTaxAutoAdjustmentsByType({
                ...plBridgeOpts,
                autoItems,
            });
        }, [plBridgeOpts, adjustmentColumnDefs]);

        const resolvePlAutoAdjForColumn = useCallback((col: AdjustmentColumnDef): number => {
            const typeKey = String(col.autoAdjType || col.refNo || '').trim();
            if (typeKey && plAutoAdjustmentsByType[typeKey] != null) {
                return plAutoAdjustmentsByType[typeKey] || 0;
            }
            const byRef = String(col.refNo || '').trim();
            return (byRef && plAutoAdjustmentsByType[byRef]) || 0;
        }, [plAutoAdjustmentsByType]);

        // 按列（headerId/refNo）递归汇总子节点 adjustment，与单元格展示规则一致
        const sumAdjustmentForColumn = useCallback((node: any, col: AdjustmentColumnDef): number => {
            if (!hasChildren(node)) {
                if (isCode(node, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) {
                    if (col.isAutoAdj) return resolvePlAutoAdjForColumn(col);
                    return (plAdjustmentsByRefNo || {})[col.refNo] || 0;
                }
                return findAdjustmentEntry(node.adjustment, col)?.adjustment ?? 0;
            }
            let sum = 0;
            for (const child of node.children || []) {
                sum += sumAdjustmentForColumn(child, col);
            }
            return sum;
        }, [plAdjustmentsByRefNo, findAdjustmentEntry, resolvePlAutoAdjForColumn]);

        // 手动 Adjust#N 列按 refNo 汇总（派生节点公式用）
        const sumManualAdjustmentForRefNo = useCallback((node: any, refNo: string): number => {
            if (!hasChildren(node)) {
                if (isCode(node, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) {
                    return (plAdjustmentsByRefNo || {})[refNo] || 0;
                }
                if (!Array.isArray(node.adjustment)) return 0;
                const adj = node.adjustment.find((a: any) => String(a?.refNo) === refNo && !isAutoAdjEntry(a));
                return adj?.adjustment ?? 0;
            }
            let sum = 0;
            for (const child of node.children || []) {
                sum += sumManualAdjustmentForRefNo(child, refNo);
            }
            return sum;
        }, [plAdjustmentsByRefNo, isAutoAdjEntry]);

        // ✅ 计算节点的 Adjust Total（所有 refNo 的 adjustment 之和）
        // BS-EQ-LFP：手动 PL-PAT Adjust# + Auto（含 TAX）之和
        const sumAllAdjustments = useCallback((node: any): number => {
            if (!hasChildren(node)) {
                if (isCode(node, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) {
                    const manual = Object.values(plAdjustmentsByRefNo || {}).reduce((s, v) => s + (v || 0), 0);
                    const auto = Object.values(plAutoAdjustmentsByType || {}).reduce((s, v) => s + (v || 0), 0);
                    return manual + auto;
                }
                // 叶子节点：直接计算所有 adjustment 之和
                if (!Array.isArray(node.adjustment)) return 0;
                return node.adjustment.reduce((sum: number, adj: any) => sum + (adj?.adjustment ?? 0), 0);
            }
            // 父节点：递归计算所有子节点的 adjustment 之和
            let sum = 0;
            for (const child of node.children || []) {
                sum += sumAllAdjustments(child);
            }
            return sum;
        }, [plAdjustmentsByRefNo, plAutoAdjustmentsByType]);

        // Compute derived BS adjustment per refNo using same formula as Client:
        // NET_CURRENT = CA - CL, NET_ASSETS = NCA + NET_CURRENT - NCL
        const computeDerivedBsAdjForRefNo = useCallback((item: any, refNo: string): number | null => {
            const CA = findNodeByCodes((fsTree || []) as any[], FS_CODE.BS.CURRENT_ASSETS);
            const CL = findNodeByCodes((fsTree || []) as any[], FS_CODE.BS.CURRENT_LIABILITIES);
            const NCA = findNodeByCodes((fsTree || []) as any[], FS_CODE.BS.NON_CURRENT_ASSETS);
            const NCL = findNodeByCodes((fsTree || []) as any[], FS_CODE.BS.NON_CURRENT_LIABILITIES);
            const getAdj = (node: any) => node ? sumManualAdjustmentForRefNo(node, refNo) : 0;

            const netCurrentAdj = getAdj(CA) - getAdj(CL);
            if (isCode(item, ...FS_CODE.BS.NET_CURRENT)) return netCurrentAdj;

            const netAssetsAdj = getAdj(NCA) + netCurrentAdj - getAdj(NCL);
            if (isCode(item, ...FS_CODE.BS.NET_ASSETS)) return netAssetsAdj;

            // BS-EQ-LFP: from PL store
            if (isCode(item, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) {
                return (plAdjustmentsByRefNo || {})[refNo] || 0;
            }

            return null;
        }, [fsTree, sumManualAdjustmentForRefNo, plAdjustmentsByRefNo]);

        const computeDerivedAutoAdjForColumn = useCallback((item: any, col: AdjustmentColumnDef): number | null => {
            // BS-EQ-LFP Auto：与 ProfitLoss PL-PAT Auto 列一致（含 TAX）
            if (isCode(item, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) {
                return resolvePlAutoAdjForColumn(col);
            }
            const CA = findNodeByCodes((fsTree || []) as any[], FS_CODE.BS.CURRENT_ASSETS);
            const CL = findNodeByCodes((fsTree || []) as any[], FS_CODE.BS.CURRENT_LIABILITIES);
            const NCA = findNodeByCodes((fsTree || []) as any[], FS_CODE.BS.NON_CURRENT_ASSETS);
            const NCL = findNodeByCodes((fsTree || []) as any[], FS_CODE.BS.NON_CURRENT_LIABILITIES);
            const getAdj = (node: any) => node ? sumAdjustmentForColumn(node, col) : 0;
            const netCurrentAdj = getAdj(CA) - getAdj(CL);
            if (isCode(item, ...FS_CODE.BS.NET_CURRENT)) return netCurrentAdj;
            const netAssetsAdj = getAdj(NCA) + netCurrentAdj - getAdj(NCL);
            if (isCode(item, ...FS_CODE.BS.NET_ASSETS)) return netAssetsAdj;
            return null;
        }, [fsTree, sumAdjustmentForColumn, resolvePlAutoAdjForColumn]);

        // Same as above but for all refNos combined (Adjust Total mode)
        const computeDerivedBsAdjTotal = useCallback((item: any): number | null => {
            const CA = findNodeByCodes((fsTree || []) as any[], FS_CODE.BS.CURRENT_ASSETS);
            const CL = findNodeByCodes((fsTree || []) as any[], FS_CODE.BS.CURRENT_LIABILITIES);
            const NCA = findNodeByCodes((fsTree || []) as any[], FS_CODE.BS.NON_CURRENT_ASSETS);
            const NCL = findNodeByCodes((fsTree || []) as any[], FS_CODE.BS.NON_CURRENT_LIABILITIES);
            const getAdj = (node: any) => node ? sumAllAdjustments(node) : 0;

            const netCurrentAdj = getAdj(CA) - getAdj(CL);
            if (isCode(item, ...FS_CODE.BS.NET_CURRENT)) return netCurrentAdj;

            const netAssetsAdj = getAdj(NCA) + netCurrentAdj - getAdj(NCL);
            if (isCode(item, ...FS_CODE.BS.NET_ASSETS)) return netAssetsAdj;

            // BS-EQ-LFP: PL-PAT 手动 Adjust# + Auto（含 TAX）合计
            if (isCode(item, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) {
                const manual = Object.values(plAdjustmentsByRefNo || {}).reduce((s, v) => s + (v || 0), 0);
                const auto = Object.values(plAutoAdjustmentsByType || {}).reduce((s, v) => s + (v || 0), 0);
                return manual + auto;
            }

            return null;
        }, [fsTree, sumAllAdjustments, plAdjustmentsByRefNo, plAutoAdjustmentsByType]);

        // Auto 列值为 0 时：若该行 Client/Audited/Last Year/opening 仍有数据，仍渲染 '-'
        const rowHasDisplayData = useCallback((node: any, isParent: boolean, isSubtotal: boolean): boolean => {
            if (Boolean(node?.adjustByPaper)) return true;
            const clientVal = bsResolveAdjustmentValue(node, 'client', computedClientMap, {
                afterTaxFromPL: resolvedAfterTaxFromPL ?? 0,
                afterTaxClientFromPL: resolvedAfterTaxClientFromPL ?? 0,
            });
            const lastYearVal = Number(node?.lastYearAudited ?? computedLastYearAuditedMap?.[Number(node?.fsId)] ?? 0) || 0;
            const auditedVal = hasChildren(node)
                ? Number(computedAuditedMap?.[Number(node?.fsId)] ?? 0) || 0
                : Number(node?.finalAmount ?? 0) || 0;
            if (Math.abs(clientVal) >= 0.01 || Math.abs(auditedVal) >= 0.01 || Math.abs(lastYearVal) >= 0.01) {
                return true;
            }
            if (!isParent && !isSubtotal) {
                const opening = Number(node?.openingAmount ?? node?.amount ?? 0) || 0;
                if (Math.abs(opening) >= 0.01) return true;
            }
            return false;
        }, [computedClientMap, computedAuditedMap, computedLastYearAuditedMap, resolvedAfterTaxFromPL, resolvedAfterTaxClientFromPL]);

        // Build adjustment columns based on backend header order (adjustment mode only)
        // When adjustTotalMode is true and there are multiple columns, show only "Adjust Total"
        const adjustmentColumns = useMemo(() => {
            if (mode !== 'adjustment') return [];

            // 原逻辑：多手动列；扩展：手动+Auto 合计达门槛时也可合并为 Adjust Total
            if (adjustTotalMode && canCollapseAdjustColumns) {
                return [{
                    key: 'adjust_total',
                    title: 'Adjust Total',
                    refNo: 'total',
                    width: 120,
                    render: (item: any, isParent: boolean, isExpanded: boolean, isSubtotal: boolean) => {
                        const isDerived = isCode(item, ...FS_CODE.BS.NET_CURRENT) ||
                            isCode(item, ...FS_CODE.BS.NET_ASSETS) ||
                            isCode(item, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD);
                        if (isDerived) {
                            const derivedVal = computeDerivedBsAdjTotal(item);
                            if (derivedVal == null) return null;
                            return (
                                <Typography variant="body2" sx={{fontSize: 14, textAlign: 'center'}}>
                                    {formatNumber(derivedVal, true)}
                                </Typography>
                            );
                        }

                        if (isParent && isExpanded && !isSubtotal) {
                            return <Box sx={{flex: 1}}/>;
                        }

                        if (isParent || isSubtotal) {
                            const parentTotal = sumAllAdjustments(item);
                            return (
                                <Typography variant="body2" sx={{fontSize: 14, textAlign: 'center'}}>
                                    {formatNumber(parentTotal, true)}
                                </Typography>
                            );
                        }

                        if (!Array.isArray(item.adjustment)) {
                            return (
                                <Typography variant="body2" sx={{fontSize: 14, textAlign: 'center'}}>
                                    {formatNumber(0, true)}
                                </Typography>
                            );
                        }

                        const totalValue = item.adjustment.reduce((sum: number, adj: any) => sum + (adj?.adjustment ?? 0), 0);

                        return (
                            <Typography variant="body2" sx={{fontSize: 14, textAlign: 'center'}}>
                                {formatNumber(totalValue, true)}
                            </Typography>
                        );
                    }
                }];
            }

            return adjustmentColumnDefs.map(col => ({
                key: col.key,
                title: col.title,
                refNo: col.refNo,
                width: 120,
                render: (item: any, isParent: boolean, isExpanded: boolean, isSubtotal: boolean) => {
                    const isDerivedManual = !col.isAutoAdj && (
                        isCode(item, ...FS_CODE.BS.NET_CURRENT) ||
                        isCode(item, ...FS_CODE.BS.NET_ASSETS) ||
                        isCode(item, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)
                    );
                    const isDerivedAuto = col.isAutoAdj && (
                        isCode(item, ...FS_CODE.BS.NET_CURRENT) ||
                        isCode(item, ...FS_CODE.BS.NET_ASSETS) ||
                        isCode(item, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)
                    );

                    if (isDerivedManual) {
                        const derivedVal = computeDerivedBsAdjForRefNo(item, col.refNo);
                        if (derivedVal == null) return null;
                        return (
                            <Typography variant="body2" sx={{fontSize: 14, textAlign: 'center'}}>
                                {formatNumber(derivedVal, true)}
                            </Typography>
                        );
                    }

                    if (isDerivedAuto) {
                        const derivedVal = computeDerivedAutoAdjForColumn(item, col);
                        if (derivedVal == null) {
                            return (
                                <Typography variant="body2" sx={{fontSize: 14, textAlign: 'center'}}>
                                    {formatNumber(0, true)}
                                </Typography>
                            );
                        }
                        return (
                            <Typography variant="body2" sx={{fontSize: 14, textAlign: 'center'}}>
                                {formatNumber(derivedVal, true)}
                            </Typography>
                        );
                    }

                    if (isParent && isExpanded && !isSubtotal) {
                        return <Box sx={{flex: 1}}/>;
                    }

                    if (isParent || isSubtotal) {
                        const parentTotal = sumAdjustmentForColumn(item, col);
                        return (
                            <Typography variant="body2" sx={{fontSize: 14, textAlign: 'center'}}>
                                {formatNumber(parentTotal, true)}
                            </Typography>
                        );
                    }

                    if (!Array.isArray(item.adjustment)) {
                        return (
                            <Typography variant="body2" sx={{fontSize: 14, textAlign: 'center'}}>
                                {formatNumber(0, true)}
                            </Typography>
                        );
                    }

                    const adj = findAdjustmentEntry(item.adjustment, col);
                    const value = adj?.adjustment ?? 0;
                    const headerId = adj?.headerId ?? col.headerId;
                    const isClickable = !!headerId
                        && typeof onAdjustmentCellClick === 'function'
                        && (col.isAutoAdj ? Math.abs(value) >= 0.01 : value !== 0);

                    return (
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: 14,
                                textAlign: 'center',
                                ...(isClickable && {
                                    cursor: 'pointer',
                                    '&:hover': {
                                        color: 'primary.main',
                                        textDecoration: 'underline',
                                    },
                                }),
                            }}
                            onClick={isClickable
                                ? () => onAdjustmentCellClick!(headerId, col.refNo, item)
                                : undefined}
                        >
                            {formatNumber(value, true)}
                        </Typography>
                    );
                },
            }));
        }, [mode, adjustmentColumnDefs, canCollapseAdjustColumns, adjustTotalMode, sumAllAdjustments, sumAdjustmentForColumn, findAdjustmentEntry, computeDerivedBsAdjForRefNo, computeDerivedBsAdjTotal, computeDerivedAutoAdjForColumn, onAdjustmentCellClick]);

        // Inline rendering (copy of TreeFsTable behavior)
        const [internalAuditedRounded, setInternalAuditedRounded] = useState<AuditedMode>('normal');
        const auditedRounded = controlledAuditedRounded !== undefined ? controlledAuditedRounded : internalAuditedRounded;
        const setAuditedRounded = onAuditedRoundedChange || setInternalAuditedRounded;
        const isRounding = auditedRounded !== 'normal';
        const isRoundedFinal = auditedRounded === 'rounded';
        const roundingGateContextRef = useRef({
            auditedRounded,
            isRoundingLoading,
            plTreeFromStore,
            sPLRoundedAfterTax,
        });
        useEffect(() => {
            roundingGateContextRef.current = {
                auditedRounded,
                isRoundingLoading,
                plTreeFromStore,
                sPLRoundedAfterTax,
            };
        }, [auditedRounded, isRoundingLoading, plTreeFromStore, sPLRoundedAfterTax]);
        useEffect(() => {
            const next: Record<number, boolean> = {};
            const walk = (nodes: any[]) => {
                for (const n of nodes || []) {
                    const hasKids = Array.isArray(n?.children) && n.children.length > 0;
                    if (hasKids) next[Number(n.fsId)] = true;
                    if (hasKids) walk(n.children as any[]);
                }
            };
            walk((fsTree || []) as any[]);
            setExpanded(next);
        }, [fsTree]);
        const fallbackName = (it: any): string => (it.name || it.aliasEn || it.nameEn || it.aliasZh || it.nameZh || it.code || '') as string;

        // 格式化数字：添加千分符和两位小数，0 显示为 '-'，负数使用括号
        const formatNumber = (n: number, showCents: boolean = true) => {
            if (n === 0 || Math.abs(n) < 0.01) {
                return '-';
            }

            const absValue = Math.abs(n);
            let formatted: string;

            if (showCents) {
                formatted = absValue.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });
            } else {
                formatted = Math.round(absValue).toLocaleString();
            }

            // 负数使用括号
            return n < 0 ? `(${formatted})` : formatted;
        };

        const toFixed2 = (n: number) => n.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
        const renderAudited = (n: number) => isRounding ? (Math.round(n)).toLocaleString() : toFixed2(n);

        const isAuditedZero = useCallback((n: number): boolean => {
            const v = Number(n) || 0;
            return Math.abs(v) < 0.01;
        }, []);

        const getAuditedValue = useCallback((node: any): number => {
            // BS-EQ-LFP: 直接使用 PL-PAT 的 Audited 值
            if (isCode(node, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) {
                return resolvedAfterTaxFromPL ?? 0;
            }

            // BS-EQ: audited = 所有子节点 audited 之和
            if (isCode(node, ...FS_CODE.BS.CAPITAL_AND_RESERVES)) {
                const kids = Array.isArray(node?.children) ? node.children : [];
                let sum = 0;
                for (const child of kids) {
                    sum += getAuditedValue(child);
                }
                return sum;
            }

            let clientVal = bsResolveAdjustmentValue(
                node,
                'client',
                computedClientMap,
                {
                    afterTaxFromPL: resolvedAfterTaxFromPL ?? 0,
                    afterTaxClientFromPL: resolvedAfterTaxClientFromPL ?? 0
                }
            );

            // ✅ adjustment 模式下 Audited = Client + Adjust Total
            // 派生节点使用公式计算 Adjust Total
            const derivedAdj = computeDerivedBsAdjTotal(node);
            let auditedVal = clientVal + (derivedAdj != null ? derivedAdj : sumAllAdjustments(node));

            // 在 fsOpeningEditMode 下，Audited = Client + 输入的adj + 历史adj
            if (fsOpeningEditMode) {
                const inputAdj = fsOpeningAmounts[Number(node?.fsId)] || 0;
                auditedVal = clientVal + inputAdj + (auditedVal - clientVal);
            }

            return auditedVal;
        }, [computedClientMap, fsOpeningEditMode, fsOpeningAmounts, resolvedAfterTaxClientFromPL, resolvedAfterTaxFromPL, sumAllAdjustments, computeDerivedBsAdjTotal]);

        // BS-EQ-AL (Retained profits): Audited (Client + Adjust) vs roll-forward (LY AL + LY LFP + LY DIV)
        // 使用未四舍五入的 Audited（getAuditedValue），与 Audited(Round)/Audited(Rounded) 展示无关
        useEffect(() => {
            if (!onRollForwardWarningChange) return;
            if (hideLastYearColumns || derivedRetainedEarningsAdj == null) {
                onRollForwardWarningChange(null);
                return;
            }
            const alNode = findNodeByCodes((fsTree || []) as any[], FS_CODE.BS.RETAINED_PROFITS_OR_LOSSES);
            if (!alNode) {
                onRollForwardWarningChange(null);
                return;
            }
            const auditedAmount = getAuditedValue(alNode);
            if (!resolveRetainedEarningsRollForwardMismatch(auditedAmount, derivedRetainedEarningsAdj)) {
                onRollForwardWarningChange(null);
                return;
            }
            const displayName = bsNameFor(alNode, auditedAmount, {
                afterTaxFromPL: resolvedAfterTaxFromPL ?? 0,
                periodType: (resolvedPeriodTypeFromPL as any) || null,
            }) || ((alNode as any).name || (alNode as any).nameEn || 'Retained profits/ Accumulated losses');
            onRollForwardWarningChange(`"${displayName}" value is not calculate from last year!`);
        }, [
            onRollForwardWarningChange,
            hideLastYearColumns,
            derivedRetainedEarningsAdj,
            fsTree,
            getAuditedValue,
            resolvedAfterTaxFromPL,
            resolvedPeriodTypeFromPL,
        ]);

        // ===== Rounding Adjustment: 递归加总子节点 rounding 值（用于父节点/BS-EQ 加总） =====
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const sumRoundingForNode = useCallback((node: any): number => {
            if (!isRounding || Object.keys(roundingAdjustments).length === 0) return 0;
            const code = String(node?.code || '');
            // BS-EQ-LFP: 其 rounding 效果已通过 sPLRoundedAfterTax 在 getRoundedAuditedValue 中体现，
            // 不经 sumRoundingForNode 重复计入，避免父节点(BS-EQ)双重计算
            if (isCode(node, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) return 0;
            // 叶子节点：直接查 roundingAdjustments
            const kids = Array.isArray(node?.children) ? node.children : [];
            if (kids.length === 0) {
                return roundingAdjustments[code] ?? 0;
            }
            // 父节点：递归累加子节点
            let sum = 0;
            for (const ch of kids) sum += sumRoundingForNode(ch);
            // 父节点自身也可能有直接的 rounding
            if (roundingAdjustments[code] != null) sum += roundingAdjustments[code];
            return sum;
        }, [isRounding, roundingAdjustments]);

        // Audited(Round) 模式：叶子节点四舍五入，父节点汇总子节点四舍五入后的值（round-then-sum）
        // 独立节点（派生公式节点）：先对组成部分四舍五入，再做公式计算
        // 派生节点公式中包含 sumRoundingForNode 以确保用户选择补充差额后，公式节点值被正确重算
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const getRoundedAuditedValue = useCallback((node: any): number => {
            // BS-EQ-LFP: 使用 PL 的 getRoundedAuditedValue(AFTER_TAX) 结果，避免 round-then-sum 与 round(sum) 差值
            if (isCode(node, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) {
                return sPLRoundedAfterTax ?? Math.round(resolvedAfterTaxFromPL ?? 0);
            }

            // 派生 BS 节点：使用四舍五入后的组成部分重新计算公式（含 rounding adjustment）
            if (isCode(node, ...FS_CODE.BS.NET_CURRENT)) {
                const CA = findNodeByCodes((fsTree || []) as any[], FS_CODE.BS.CURRENT_ASSETS);
                const CL = findNodeByCodes((fsTree || []) as any[], FS_CODE.BS.CURRENT_LIABILITIES);
                return (CA ? getRoundedAuditedValue(CA) + sumRoundingForNode(CA) : 0)
                    - (CL ? getRoundedAuditedValue(CL) + sumRoundingForNode(CL) : 0);
            }
            if (isCode(node, ...FS_CODE.BS.NET_ASSETS)) {
                const NCA = findNodeByCodes((fsTree || []) as any[], FS_CODE.BS.NON_CURRENT_ASSETS);
                const NCL = findNodeByCodes((fsTree || []) as any[], FS_CODE.BS.NON_CURRENT_LIABILITIES);
                const CA = findNodeByCodes((fsTree || []) as any[], FS_CODE.BS.CURRENT_ASSETS);
                const CL = findNodeByCodes((fsTree || []) as any[], FS_CODE.BS.CURRENT_LIABILITIES);
                const netCurrent = (CA ? getRoundedAuditedValue(CA) + sumRoundingForNode(CA) : 0)
                    - (CL ? getRoundedAuditedValue(CL) + sumRoundingForNode(CL) : 0);
                return (NCA ? getRoundedAuditedValue(NCA) + sumRoundingForNode(NCA) : 0)
                    + netCurrent
                    - (NCL ? getRoundedAuditedValue(NCL) + sumRoundingForNode(NCL) : 0);
            }

            const kids = Array.isArray(node?.children) ? node.children : [];
            if (kids.length === 0) {
                return Math.round(getAuditedValue(node));
            }
            let sum = 0;
            for (const child of kids) {
                sum += getRoundedAuditedValue(child);
            }
            return sum;
        }, [getAuditedValue, fsTree, sPLRoundedAfterTax, resolvedAfterTaxFromPL, sumRoundingForNode]);

        // ===== Rounding Adjustment: 缓存候选节点列表（只在树结构变化时重建） =====
        // MULTI：PL 按 periods[].plLine 分 YOA 收集；SINGLE：engagement plLine
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const roundingCandidateNodes = useMemo(() => {
            if (!fsTree || fsTree.length === 0) return [];
            const nodes: RoundingTargetNode[] = [];
            const collectLeaves = (
                tree: any[],
                statement: 'BS' | 'PL',
                parentGroupName: string = '',
                parentCode: string = '',
                yearOfAssessment: string | null = null,
            ) => {
                for (const node of tree || []) {
                    const kids = Array.isArray(node?.children) ? node.children : [];
                    const groupName = (node.level === 2 ? (node.name || node.nameEn || node.code || '') : parentGroupName);
                    const pCode = (node.level === 2 ? (node.code || '') : parentCode);
                    if (kids.length === 0 && node.level === 3) {
                        const val = statement === 'BS'
                            ? getAuditedValue(node)
                            : (Number(node.finalAmount) || Number(node.openingAmount) || 0);
                        nodes.push({
                            fsId: Number(node.fsId),
                            code: node.code || '',
                            name: node.name || node.nameEn || '',
                            statement,
                            currentRoundedValue: Math.round(val),
                            groupName,
                            parentCode: pCode,
                            yearOfAssessment: statement === 'PL' ? yearOfAssessment : null,
                        });
                    }
                    if (kids.length > 0) collectLeaves(kids, statement, groupName, pCode, yearOfAssessment);
                }
            };
            collectLeaves(fsTree as any[], 'BS');
            const isMultiPl = adjustSpanTypeForRounding === 'MULTI'
                && Array.isArray(adjustPeriodsForRounding)
                && adjustPeriodsForRounding.length >= 2;
            if (isMultiPl) {
                for (const period of adjustPeriodsForRounding) {
                    const yoa = String(period?.yearOfAssessment || '').trim() || null;
                    const periodPl = Array.isArray(period?.plLine) ? period.plLine : [];
                    if (!yoa || periodPl.length === 0) continue;
                    const plTree = periodPl.map(convertAuditFsItemVOToFsSimpleData);
                    collectLeaves(plTree, 'PL', '', '', yoa);
                }
            } else if (plTreeFromStore) {
                const plTree = (plTreeFromStore || []).map(convertAuditFsItemVOToFsSimpleData);
                collectLeaves(plTree, 'PL');
            }
            return nodes;
        }, [fsTree, plTreeFromStore, getAuditedValue, adjustSpanTypeForRounding, adjustPeriodsForRounding]);

        // 与 Audited(Round) 列渲染一致：含 rounding adjustment 汇总后的展示值
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const getRoundedDisplayAuditedValue = useCallback((node: any): number => {
            if (!isRounding) return Math.round(getAuditedValue(node));

            if (isCode(node, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) {
                const lfpCode = FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD[0];
                const profitImpact = roundingAdjustments[lfpCode] ?? 0;
                const roundingAdj = roundingAdjustments[lfpCode] ?? 0;
                return getRoundedAuditedValue(node) - profitImpact + roundingAdj;
            }

            const isDerivedItem = isCode(
                node,
                ...FS_CODE.BS.NET_CURRENT,
                ...FS_CODE.BS.NET_ASSETS,
                ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD,
            );
            if (hasChildren(node) || isDerivedItem) {
                return getRoundedAuditedValue(node) + sumRoundingForNode(node);
            }

            const code = String(node?.code || '');
            return getRoundedAuditedValue(node) + (roundingAdjustments[code] ?? 0);
        }, [isRounding, getAuditedValue, getRoundedAuditedValue, sumRoundingForNode, roundingAdjustments]);

        // BS-NL-RES 与 BS-EQ 差额（含 rounding 后展示值）
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const computeBsRoundingBalanceDiff = useCallback((): number | null => {
            if (!fsTree || fsTree.length === 0) return null;
            const netAssetsNode = findNodeByCodes(fsTree as any[], FS_CODE.BS.NET_ASSETS);
            const capResNode = findNodeByCodes(fsTree as any[], FS_CODE.BS.CAPITAL_AND_RESERVES);
            if (!netAssetsNode || !capResNode) return null;
            return getRoundedDisplayAuditedValue(netAssetsNode) - getRoundedDisplayAuditedValue(capResNode);
        }, [fsTree, getRoundedDisplayAuditedValue]);

        // 与 rounding gate isDataReady 一致：PL 未就绪时不计算差额，避免短暂非 0 误开弹窗
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const isRoundingDiffReady = useCallback((): boolean => {
            if (auditedRounded !== 'round') return false;
            if (isRoundingLoading) return false;
            if (plTreeFromStore?.length && sPLRoundedAfterTax == null) return false;
            return true;
        }, [auditedRounded, isRoundingLoading, plTreeFromStore, sPLRoundedAfterTax]);

        // ===== Rounding Adjustment: 差值检测 + 统一 gate 控制器注册 =====
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const openRoundingDialogIfNeeded = useCallback(() => {
            if (roundingDialogDismissedRef.current) return;
            const diff = computeBsRoundingBalanceDiff();
            if (diff == null || diff === 0) return;
            const nodes = prepareRoundingTargetNodes(roundingCandidateNodes);
            setRoundingTargetNodes(nodes);
            setRoundingDialogOpen(true);
        }, [roundingCandidateNodes, computeBsRoundingBalanceDiff]);

        const openRoundingDialogRef = useRef(openRoundingDialogIfNeeded);
        openRoundingDialogRef.current = openRoundingDialogIfNeeded;

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            registerRoundingGateController({
                openDialog: () => openRoundingDialogRef.current(),
                closeDialog: () => setRoundingDialogOpen(false),
                isDataReady: () => {
                    const ctx = roundingGateContextRef.current;
                    if (ctx.auditedRounded !== 'round') return false;
                    if (ctx.isRoundingLoading) return false;
                    if (ctx.plTreeFromStore?.length && ctx.sPLRoundedAfterTax == null) return false;
                    return true;
                },
                isDismissed: () => roundingDialogDismissedRef.current,
                clearDismissed: () => {
                    roundingDialogDismissedRef.current = false;
                },
            });
            return () => unregisterRoundingGateController();
        }, []);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            if (auditedRounded !== 'round' || !fsTree || fsTree.length === 0) {
                if (auditedRounded !== 'round') {
                    roundingDialogDismissedRef.current = false;
                    setRoundingDialogOpen(false);
                    resetLatestRoundingDifference();
                }
                return;
            }
            if (!isRoundingDiffReady()) return;

            const diff = computeBsRoundingBalanceDiff();
            if (diff == null) return;

            setRoundingDifference(diff);
            setLatestRoundingDifference(diff);
            if (diff === 0) {
                setRoundingDialogOpen(false);
            }
        }, [auditedRounded, fsTree, isRoundingLoading, roundingAdjustments, sPLRoundedAfterTax, computeBsRoundingBalanceDiff, isRoundingDiffReady]);

        // PL rounded 就绪后重试尚未完成的 gate
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            if (!isRoundingDiffReady()) return;
            retryActiveRoundingGate();
        }, [isRoundingDiffReady, sPLRoundedAfterTax]);

        // ===== Rounding: 是否存在已保存的 rounding 数据（决定三态切换是否可用） =====
        const hasRoundingRecords = hasRoundingTargets(roundingGroup);
        const cycleAuditedMode = useCallback(() => {
            if (auditedRounded === 'normal') {
                if (!onAuditedRoundedChange) {
                    requestAuditedRoundingGate();
                }
                setAuditedRounded('round');
            } else if (auditedRounded === 'round') {
                if (hasRoundingRecords) {
                    setAuditedRounded('rounded');
                } else {
                    setAuditedRounded('normal');
                }
            } else {
                setAuditedRounded('normal');
            }
        }, [auditedRounded, hasRoundingRecords, setAuditedRounded, onAuditedRoundedChange]);

        const handleRoundingDialogClose = useCallback(() => {
            roundingDialogDismissedRef.current = true;
            setRoundingDialogOpen(false);
            notifyRoundingDialogCancelled();
        }, []);

        // ===== Rounding Adjustment: 用户确认回调（乐观更新 React Query 缓存 + 回滚） =====
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const handleRoundingAdjustApply = useCallback((entries: RoundingApplyEntry[]) => {
            roundingAdjustService.save({
                annualId: Number(annualId),
                columnType: 1,
                targets: buildRoundingSaveTargets(entries),
            }).then((saved) => {
                queryClient.setQueryData(['rounding-adjust', Number(annualId)], saved);
                queryClient.invalidateQueries({queryKey: ['adjustment', 'list']});
                setRoundingDialogOpen(false);
                notifyRoundingSaved();
            }).catch(() => {
                // 回滚：清除缓存，让下次自动 refetch
                queryClient.invalidateQueries({queryKey: ['rounding-adjust', Number(annualId)]});
                roundingDialogDismissedRef.current = false;
                setRoundingDialogOpen(true);
            });
        }, [annualId, queryClient]);

        // ===== Rounding Adjustment: 删除回调（乐观清空 React Query 缓存 + 回滚） =====
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const handleRoundingRemove = useCallback(() => {
            // 乐观清空缓存
            queryClient.setQueryData(['rounding-adjust', Number(annualId)], emptyRoundingAdjustGroup(Number(annualId)));
            // 异步删后端
            roundingAdjustService.remove(Number(annualId)).then(() => {
                // 无感刷新：主调整列表后台 refetch，派生计算回到删除前状态
                queryClient.invalidateQueries({queryKey: ['adjustment', 'list']});
            }).catch(() => {
                // 回滚：让 React Query 重新 fetch
                queryClient.invalidateQueries({queryKey: ['rounding-adjust', Number(annualId)]});
            });
        }, [annualId, queryClient]);

        // Visibility rule (adjustment mode only):
        // - Leaf: hide if audited/client/lastYear all 0 (and no adj)
        // - Parent: hide only if all descendants hidden
        // - Exception: BS-EQ-IC (Issued Capital) always visible even when Client/Audited are 0
        // - Exception: BS-CA-DPP — REN 押金仅在 WP 合成、FS amount 常为 0；wpStatus 非 NOT_STARTED 时仍渲染
        const visibleByFsId = useMemo(() => {
            const out: Record<number, boolean> = {};
            const visit = (node: any, forceVisible: boolean = false): boolean => {
                // BS-NCA 节点下的所有子节点都不做隐藏
                const isNCA = isCode(node, ...FS_CODE.BS.NON_CURRENT_ASSETS);
                const childForceVisible = forceVisible || isNCA;

                const kids: any[] = Array.isArray(node?.children) ? node.children : [];
                let hasVisibleChild = false;
                for (const ch of kids) {
                    if (visit(ch, childForceVisible)) hasVisibleChild = true;
                }

                if (forceVisible) {
                    out[Number(node?.fsId)] = true;
                    return true;
                }

                let auditedVal = getAuditedValue(node);
                // Round 模式下：含 rounding 后值非零的节点也需要渲染
                if (isRounding) {
                    const code = String(node?.code || '');
                    const roundAdj = roundingAdjustments[code] ?? 0;
                    if (roundAdj !== 0) auditedVal += roundAdj;
                    // 父节点累加子节点 rounding
                    if (kids.length > 0) auditedVal += sumRoundingForNode(node);
                }
                const lastYearVal = node.lastYearAudited;
                // adjustment 模式下，client / adjust / audited 中任意一个有值时都需要渲染
                const clientVal = bsResolveAdjustmentValue(node, 'client', computedClientMap, {
                    afterTaxFromPL: resolvedAfterTaxFromPL ?? 0,
                    afterTaxClientFromPL: resolvedAfterTaxClientFromPL ?? 0
                });
                const hasNonZeroAdj = Array.isArray(node.adjustment) && node.adjustment.some((a: any) => Math.abs(a?.adjustment ?? 0) >= 0.01);
                const hasAdjustByPaper = Boolean(node?.adjustByPaper);
                const hasRoundingAdj = isRounding && (roundingAdjustments[String(node?.code || '')] ?? 0) !== 0;
                // Issued Capital：Client/Audited 为 0 时仍常规渲染（SINGLE / MULTI 共用本表）
                const isIssuedCapital = isCode(node, ...FS_CODE.BS.ISSUED_CAPITAL);
                // Deposit paid：后端 hasData（REN 押金）会置 adjustByPaper；wpStatus 已同步时亦渲染
                const isDepositPaid = isCode(node, 'BS-CA-DPP');
                const hasDepositPaidWpTrack = isDepositPaid
                    && node?.wpStatus != null
                    && Number(node.wpStatus) !== 0;
                // 仅当 client / audited / last year / 非零 adjust 有值时显示；Auto 占位（值为 0）不触发显示
                const selfVisible = isIssuedCapital
                    || hasDepositPaidWpTrack
                    || !isAuditedZero(auditedVal) || !isAuditedZero(lastYearVal) || !isAuditedZero(clientVal)
                    || hasNonZeroAdj || hasAdjustByPaper || hasRoundingAdj;
                const visible = selfVisible || hasVisibleChild;
                out[Number(node?.fsId)] = visible;
                return visible;
            };

            for (const root of (fsTree || []) as any[]) {
                visit(root);
            }
            return out;
        }, [fsTree, getAuditedValue, isAuditedZero, computedLastYearAuditedMap, computedClientMap, resolvedAfterTaxFromPL, resolvedAfterTaxClientFromPL, isRounding, roundingAdjustments, sumRoundingForNode]);

        const rows = useMemo(() => flatten((fsTree || []) as any[], 0), [fsTree, expanded]);

        // 计算表格列数（用于 Collapse 的 colSpan）
        const totalColumns = useMemo(() => {
            let count = 4; // title + chip + Client + Audited
            if (fsOpeningEditMode) count += 1;
            count += adjustmentColumns.length;
            if (!hideLastYearColumns) count += 2; // Last Year + Change
            return count;
        }, [fsOpeningEditMode, adjustmentColumns.length, hideLastYearColumns]);

        // 将扁平化的 rows 按 parentFsId 分组，用于 Collapse 动画
        const groupedRows = useMemo(() => {
            const groups: Array<{
                parentFsId: number | null;
                rows: typeof rows;
                isExpanded: boolean;
            }> = [];

            let currentGroup: typeof rows = [];
            let currentParentFsId: number | null = null;

            for (const row of rows) {
                if (row.parentFsId !== currentParentFsId) {
                    if (currentGroup.length > 0) {
                        groups.push({
                            parentFsId: currentParentFsId,
                            rows: currentGroup,
                            isExpanded: currentParentFsId !== null ? !!expanded[currentParentFsId] : true
                        });
                    }
                    currentGroup = [row];
                    currentParentFsId = row.parentFsId;
                } else {
                    currentGroup.push(row);
                }
            }

            if (currentGroup.length > 0) {
                groups.push({
                    parentFsId: currentParentFsId,
                    rows: currentGroup,
                    isExpanded: currentParentFsId !== null ? !!expanded[currentParentFsId] : true
                });
            }

            return groups;
        }, [rows, expanded]);

        const isZeroish = (node: React.ReactNode): boolean => {
            if (node === null || node === undefined) return false;
            if (typeof node === 'number') return node === 0;
            if (typeof node === 'string') {
                const cleaned = node.replace(/,/g, '').trim();
                if (cleaned === '') return false;
                const n = Number(cleaned);
                return !Number.isNaN(n) && n === 0;
            }
            if (React.isValidElement(node)) {
                const anyProps: any = (node as any).props || {};
                const v = anyProps.value;
                if (typeof v === 'number' && v === 0) return true;
                if (typeof v === 'string') {
                    const cleaned = v.replace(/,/g, '').trim();
                    if (cleaned !== '' && !Number.isNaN(Number(cleaned)) && Number(cleaned) === 0) return true;
                }
                const ch = anyProps.children;
                if (typeof ch === 'string') {
                    const cleaned = ch.replace(/,/g, '').trim();
                    if (cleaned !== '' && !Number.isNaN(Number(cleaned)) && Number(cleaned) === 0) return true;
                }
            }
            return false;
        };
        // filterNonEmpty and visible columns removed (draft, review, history)

        return (
            <Box
                ref={fillRootRef}
                sx={{
                width: '100%',
                height: '100%',
                maxHeight: fillHeightPx != null ? fillHeightPx : undefined,
                minHeight: 0,
                overflow: 'hidden',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <Stack direction="row" justifyContent="flex-start" alignItems="flex-start" spacing={4} mb={0.5}>
                    <Box sx={{minWidth: 0}}>
                        {!!resolvedCompanyName && (
                            <Typography variant="h6" fontWeight={700} mb={0.5}>{resolvedCompanyName}</Typography>)}
                        <Typography variant="subtitle1" fontWeight={600} mb={0.5}>Balance Sheet items</Typography>
                        {asAtPeriodTo && (
                            <Typography variant="subtitle1" fontWeight={700} mb={1}>As
                                at {formatAsAtDate(asAtPeriodTo)}</Typography>
                        )}
                    </Box>
                    <FsMaterialityHeader
                        companyId={resolvedCompanyId}
                        annualId={annualId}
                        statement="BS"
                    />
                </Stack>
                <Divider sx={{mb: 1}}/>
                <TableContainer ref={tableContainerRef} component={Paper} sx={{flex: 1, minHeight: 0, overflow: 'auto'}}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                {/*title*/}
                                <TableCell sx={mode === 'adjustment'
                                    ? {
                                        position: 'sticky',
                                        left: 0,
                                        top: 0,
                                        zIndex: 4,
                                        backgroundColor: 'background.paper',
                                        width: 400,
                                        minWidth: 400,
                                        maxWidth: 400,
                                        px: 0
                                    }
                                    : {width: '18%'}}/>
                                {/*chip*/}
                                <TableCell align="center" sx={mode === 'adjustment'
                                    ? {
                                        position: 'sticky',
                                        left: 400,
                                        top: 0,
                                        zIndex: 4,
                                        backgroundColor: 'background.paper',
                                        width: 50,
                                        minWidth: 50,
                                        maxWidth: 50,
                                        px: 0
                                    }
                                    : {width: 65, px: 0}}/>
                                <TableCell align="center" sx={{width: 160, fontWeight: 700}}>
                                    Client
                                    {columnHeaderYoa && <Typography variant="caption" sx={{
                                        fontSize: 12,
                                        color: 'text.secondary',
                                        display: 'block',
                                        lineHeight: 1
                                    }}>{columnHeaderYoa}</Typography>}
                                    {presentationCurrency && <Typography variant="caption" sx={{
                                        fontSize: 12,
                                        color: 'text.secondary',
                                        display: 'block',
                                        lineHeight: 1
                                    }}>{presentationCurrency}</Typography>}
                                </TableCell>
                                {fsOpeningEditMode && (
                                    <TableCell align="center" sx={{
                                        width: 160,
                                        fontWeight: 700,
                                        bgcolor: 'primary.light',
                                        color: 'white'
                                    }}>
                                        Adjustment
                                        {columnHeaderYoa && <Typography variant="caption" sx={{
                                            fontSize: 12,
                                            display: 'block',
                                            lineHeight: 1,
                                            opacity: 0.85
                                        }}>{columnHeaderYoa}</Typography>}
                                        {presentationCurrency && <Typography variant="caption" sx={{
                                            fontSize: 12,
                                            display: 'block',
                                            lineHeight: 1,
                                            opacity: 0.85
                                        }}>{presentationCurrency}</Typography>}
                                    </TableCell>
                                )}
                                {adjustmentColumns.map((col: any) => (
                                    <TableCell
                                        align="center"
                                        key={col.key}
                                        sx={[
                                            {fontWeight: 700, width: col.width},
                                            canCollapseAdjustColumns ? adjustColumnToggleHeaderSx : {},
                                        ]}
                                        onClick={canCollapseAdjustColumns ? () => setAdjustTotalMode(!adjustTotalMode) : undefined}
                                    >
                                        {col.title}
                                        {columnHeaderYoa && <Typography variant="caption" sx={{
                                            fontSize: 12,
                                            color: 'text.secondary',
                                            display: 'block',
                                            lineHeight: 1
                                        }}>{columnHeaderYoa}</Typography>}
                                        {presentationCurrency && <Typography variant="caption" sx={{
                                            fontSize: 12,
                                            color: 'text.secondary',
                                            display: 'block',
                                            lineHeight: 1
                                        }}>{presentationCurrency}</Typography>}
                                    </TableCell>
                                ))}
                                <TableCell align="center" sx={{width: 160, fontWeight: 700, cursor: 'pointer'}}
                                           onClick={cycleAuditedMode}>
                                    {auditedRounded === 'normal' ? 'Audited' : auditedRounded === 'round' ? 'Audited (Round)' : 'Audited (Rounded)'}
                                    {auditedRounded === 'round' && roundingDifference !== 0 && Object.keys(roundingAdjustments).length === 0 && (
                                        <Typography component="span" sx={{
                                            ml: 0.5,
                                            color: 'warning.main',
                                            fontSize: 14,
                                            cursor: 'pointer'
                                        }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        requestAuditedRoundingGate();
                                                    }}>
                                            ⚠️
                                        </Typography>
                                    )}
                                    {columnHeaderYoa && <Typography variant="caption" sx={{
                                        fontSize: 12,
                                        color: 'text.secondary',
                                        display: 'block',
                                        lineHeight: 1
                                    }}>{columnHeaderYoa}</Typography>}
                                    {presentationCurrency && <Typography variant="caption" sx={{
                                        fontSize: 12,
                                        color: 'text.secondary',
                                        display: 'block',
                                        lineHeight: 1
                                    }}>{presentationCurrency}</Typography>}
                                </TableCell>
                                {!hideLastYearColumns && (
                                    <>
                                        <TableCell align="center" sx={{width: 160, fontWeight: 700, ...lastYearColumnSeparatorSx}}>
                                            Last Year
                                            {lastYearOfAssessment && <Typography variant="caption" sx={{
                                                fontSize: 12,
                                                color: 'text.secondary',
                                                display: 'block',
                                                lineHeight: 1
                                            }}>{lastYearOfAssessment}</Typography>}
                                            {presentationCurrency && <Typography variant="caption" sx={{
                                                fontSize: 12,
                                                color: 'text.secondary',
                                                display: 'block',
                                                lineHeight: 1
                                            }}>{presentationCurrency}</Typography>}
                                        </TableCell>
                                        <TableCell align="center" sx={{width: 120, fontWeight: 700}}>Change</TableCell>
                                    </>
                                )}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading && (
                                <FsStatementTableSkeletonRows
                                    columns={buildAdjustmentSkeletonColumns({
                                        statement: 'bs',
                                        adjustColumnCount: adjustmentColumns.length,
                                        hideLastYearColumns,
                                        fsOpeningEditMode,
                                    })}
                                />
                            )}
                            {!loading && rows.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={4 + adjustmentColumns.length}
                                        align="center">No data</TableCell>
                                </TableRow>
                            ) : (
                                rows.map(({item, level, isParent, isSubtotal, parentItem}) => {
                                    const isExpanded = !!expanded[Number(item.fsId)];

                                    // Hide 0-audited nodes in adjustment mode
                                    if (!isSubtotal && mode === 'adjustment') {
                                        const visible = visibleByFsId[Number(item.fsId)];
                                        if (visible === false) return null;
                                    }

                                    // Check if item has adjustByPaper = true (adjustment mode only)
                                    const hasAdjustByPaper = mode === 'adjustment' && (item as any)?.adjustByPaper === true;

                                    // 检查是否应该排除该 schedule_code（使用顶层定义的 currentMode）
                                    const shouldExclude = (currentMode === AdjustmentMode.ADJUST_WP_TO_WP);
                                    // && excludedScheduleCodes.includes(String(item?.scheduleCode || '').trim());

                                    // BS-CL-OFL / BS-NCL-OFL：adjustment 模式下 perClient (openingAmount) 或 wpStatus 有值时显示 chip
                                    // BS-NCL-OFL 与 BS-CL-OFL 共享 WP：CL 有值时 NCL 也应显示 chip
                                    const financeLeaseLineCode = normalizeLineCode((item as any)?.code || (item as any)?.scheduleCode);
                                    const isVehicleLoanFinanceLease = isCode(
                                        financeLeaseLineCode,
                                        ...FS_CODE.BS.VEHICLE_LOAN_CURRENT,
                                        ...FS_CODE.BS.VEHICLE_LOAN_NON_CURRENT,
                                    );
                                    const isVehicleLoanNonCurrent = isCode(financeLeaseLineCode, ...FS_CODE.BS.VEHICLE_LOAN_NON_CURRENT);
                                    const shouldHideVehicleLoanChip = mode === 'adjustment'
                                        && isVehicleLoanFinanceLease
                                        && !vehicleLoanChipHasTrackableValue(item)
                                        && !(isVehicleLoanNonCurrent && vehicleLoanChipHasTrackableValue(vehicleLoanCurrentLine));
                                    const scheduleChipItem = (() => {
                                        if (!isVehicleLoanNonCurrent
                                            || vehicleLoanChipHasTrackableValue(item)
                                            || !vehicleLoanCurrentLine) {
                                            return item;
                                        }
                                        return {
                                            ...item,
                                            openingAmount: Number(vehicleLoanCurrentLine?.openingAmount ?? 0) || item.openingAmount,
                                            wpStatus: item.wpStatus ?? vehicleLoanCurrentLine?.wpStatus ?? null,
                                            wpIssueRemark: item.wpIssueRemark ?? vehicleLoanCurrentLine?.wpIssueRemark ?? null,
                                            outstanding: item.outstanding ?? vehicleLoanCurrentLine?.outstanding ?? null,
                                            outstandingRemark: item.outstandingRemark ?? vehicleLoanCurrentLine?.outstandingRemark ?? null,
                                        };
                                    })();

                                    const showSchedule = (!isParent && !!item.scheduleName) && !shouldExclude && !shouldHideVehicleLoanChip && (
                                        hasAdjustByPaper || (!resolvedAllowedScheduleCodes ? true : (
                                            resolvedAllowedScheduleCodes.has(normalizeLineCode((item as any)?.code || (item as any)?.scheduleCode)) ||
                                            resolvedAllowedScheduleCodes.has(String((item as any)?.scheduleCode || '').trim())
                                        ))
                                    );

                                    // 使用统一的 resolve 函数计算所有节点的值（包括派生节点）
                                    let clientVal = bsResolveAdjustmentValue(
                                        item,
                                        'client',
                                        computedClientMap,
                                        {
                                            afterTaxFromPL: resolvedAfterTaxFromPL ?? 0,
                                            afterTaxClientFromPL: resolvedAfterTaxClientFromPL ?? 0
                                        }
                                    );

                                    // ✅ 在 adjustment 模式下，Audited = Client + Adjust Total
                                    // 派生节点使用公式计算 Adjust Total
                                    // BS-EQ-LFP: 直接使用 PL-PAT 的 Audited 值
                                    // BS-EQ: audited = 所有子节点 audited 之和
                                    let auditedVal: number;
                                    if (mode === 'adjustment') {
                                        if (isCode(item, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) {
                                            auditedVal = resolvedAfterTaxFromPL ?? 0;
                                        } else if (isCode(item, ...FS_CODE.BS.CAPITAL_AND_RESERVES)) {
                                            auditedVal = getAuditedValue(item);
                                        } else {
                                            const derivedAdj = computeDerivedBsAdjTotal(item);
                                            const adjustTotal = derivedAdj != null ? derivedAdj : sumAllAdjustments(item);
                                            auditedVal = clientVal + adjustTotal;
                                        }
                                    } else {
                                        auditedVal = bsResolveAdjustmentValue(
                                            item,
                                            'audited',
                                            computedAuditedMap,
                                            {
                                                afterTaxFromPL: resolvedAfterTaxFromPL ?? 0,
                                                afterTaxClientFromPL: resolvedAfterTaxClientFromPL ?? 0
                                            }
                                        );
                                    }

                                    // 在 fsOpeningEditMode 下，Audited = Client + 输入的adj + 历史adj
                                    if (fsOpeningEditMode) {
                                        const inputAdj = fsOpeningAmounts[Number(item.fsId)] || 0;
                                        auditedVal = clientVal + inputAdj + (auditedVal - clientVal);
                                    }

                                    // ===== Rounding: 父节点使用 round-then-sum, 派生节点使用公式重算 =====
                                    if (isRounding) {
                                        const isDerivedItem = isCode(item, ...FS_CODE.BS.NET_CURRENT) ||
                                            isCode(item, ...FS_CODE.BS.NET_ASSETS) ||
                                            isCode(item, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD);
                                        if (isCode(item, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) {
                                            // BS-EQ-LFP: sPLRoundedAfterTax 已含 PL 端 rounding 传播，
                                            // 减去 profit impact 得到 base，渲染层会加回 roundingAdj
                                            const profitImpact = roundingAdjustments[FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD[0]] ?? 0;
                                            auditedVal = getRoundedAuditedValue(item) - profitImpact;
                                        } else if (hasChildren(item) || isDerivedItem) {
                                            auditedVal = getRoundedAuditedValue(item) + sumRoundingForNode(item);
                                        }
                                    }

                                    // 如果是小计行，使用父节点的计算值
                                    if (isSubtotal && parentItem) {
                                        clientVal = bsResolveAdjustmentValue(
                                            parentItem,
                                            'client',
                                            computedClientMap,
                                            {
                                                afterTaxFromPL: resolvedAfterTaxFromPL ?? 0,
                                                afterTaxClientFromPL: resolvedAfterTaxClientFromPL ?? 0
                                            }
                                        );

                                        // ✅ 小计行也使用 Client + Adjust Total
                                        if (mode === 'adjustment') {
                                            if (isCode(parentItem, ...FS_CODE.BS.CAPITAL_AND_RESERVES)) {
                                                auditedVal = getAuditedValue(parentItem);
                                            } else {
                                                const derivedAdjSub = computeDerivedBsAdjTotal(parentItem);
                                                const adjustTotal = derivedAdjSub != null ? derivedAdjSub : sumAllAdjustments(parentItem);
                                                auditedVal = clientVal + adjustTotal;
                                            }
                                        } else {
                                            auditedVal = bsResolveAdjustmentValue(
                                                parentItem,
                                                'audited',
                                                computedAuditedMap,
                                                {
                                                    afterTaxFromPL: resolvedAfterTaxFromPL ?? 0,
                                                    afterTaxClientFromPL: resolvedAfterTaxClientFromPL ?? 0
                                                }
                                            );
                                        }

                                        if (fsOpeningEditMode) {
                                            const inputAdj = fsOpeningAmounts[Number(parentItem.fsId)] || 0;
                                            auditedVal = clientVal + inputAdj + (auditedVal - clientVal);
                                        }

                                        // ===== Rounding: 小计行使用父节点 round-then-sum / 派生节点公式重算 =====
                                        if (isRounding) {
                                            const parentIsDerived = isCode(parentItem, ...FS_CODE.BS.NET_CURRENT) ||
                                                isCode(parentItem, ...FS_CODE.BS.NET_ASSETS) ||
                                                isCode(parentItem, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD);
                                            if (isCode(parentItem, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) {
                                                const profitImpact = roundingAdjustments[FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD[0]] ?? 0;
                                                auditedVal = getRoundedAuditedValue(parentItem) - profitImpact;
                                            } else if (hasChildren(parentItem) || parentIsDerived) {
                                                auditedVal = getRoundedAuditedValue(parentItem) + sumRoundingForNode(parentItem);
                                            }
                                        }
                                    }

                                    // Subtotal row: hide if audited total is 0
                                    if (isSubtotal) {
                                        const lastYearSubtotalVal = parentItem ? (computedLastYearAuditedMap?.[Number(parentItem.fsId)] ?? 0) : 0;
                                        if (isAuditedZero(auditedVal) && isAuditedZero(lastYearSubtotalVal)) {
                                            return null;
                                        }
                                    }

                                    // 名称生成：使用计算出的 auditedVal
                                    const name = ((): string => {
                                        if (isSubtotal) return ''; // 小计行显示空白名称
                                        const nameAmount = auditedVal;
                                        return bsNameFor(item, nameAmount, {
                                            afterTaxFromPL: Number(resolvedAfterTaxFromPL ?? 0),
                                            periodType: (resolvedPeriodTypeFromPL as any) || null,
                                        }) || fallbackName(item);
                                    })();
                                    return (
                                        <TableRow key={item.fsId} hover={!isSubtotal} sx={{
                                            '& .MuiTableCell-root': {
                                                paddingTop: '0.5px',
                                                paddingBottom: '0.5px',
                                                ...((item as any).level === 2 && !isParent && !isSubtotal && {
                                                    paddingTop: '5px',
                                                    paddingBottom: '5px',
                                                }),
                                                ...(isSubtotal && {
                                                    borderBottom: '2px solid',
                                                    borderBottomColor: 'divider',
                                                    fontWeight: 600
                                                })
                                            },
                                            ...(mode === 'adjustment' && !isSubtotal && {
                                                '&:hover > .MuiTableCell-root:nth-of-type(-n+2)': {
                                                    backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.04), rgba(0, 0, 0, 0.04))',
                                                }
                                            })
                                        }}>
                                            <TableCell sx={mode === 'adjustment'
                                                ? {
                                                    position: 'sticky',
                                                    left: 0,
                                                    zIndex: 1,
                                                    backgroundColor: 'background.paper',
                                                    width: 250,
                                                    minWidth: 250,
                                                    maxWidth: 250
                                                }
                                                : undefined}>
                                                <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                    <Box sx={{pl: level * 2}}/>
                                                    {isParent && !isSubtotal ? (
                                                        <IconButton size="small"
                                                                    onClick={() => toggleNode(Number(item.fsId))}>
                                                            {isExpanded ? <ExpandLessIcon fontSize="small"/> :
                                                                <ExpandMoreIcon fontSize="small"/>}
                                                        </IconButton>
                                                    ) : (
                                                        <Box sx={{width: 15}}/>
                                                    )}
                                                    <InlineEditableName
                                                        value={name}
                                                        placeholder={String((item as any).nameEn || (item as any).nameZh || item.code || '')}
                                                        editable={
                                                            mode === 'adjustment'
                                                            && itemAliasEditEnabled
                                                            && !readOnly
                                                            && !isSubtotal
                                                            && canEditItemAlias(item)
                                                        }
                                                        locked={
                                                            mode === 'adjustment'
                                                            && itemAliasEditEnabled
                                                            && !readOnly
                                                            && !isSubtotal
                                                            && isItemAliasLocked(item)
                                                        }
                                                        lockedItemLabel={name || String(item.code || 'item')}
                                                        typographySx={{
                                                            mr: 1,
                                                            ...((isSubtotal || (item as any).level === 2) && {fontWeight: 600}),
                                                            ...((item as any).level === 2 && {fontFamily: '"Times New Roman", serif'}),
                                                        }}
                                                        onCommit={async (next) => {
                                                            if (!onItemAliasChange || !item.fsId || !item.code) return;
                                                            await onItemAliasChange({
                                                                fsId: Number(item.fsId),
                                                                code: String(item.code),
                                                                statement: 'BS',
                                                                displayName: next,
                                                            });
                                                        }}
                                                    />
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center" sx={mode === 'adjustment'
                                                ? {
                                                    position: 'sticky',
                                                    left: 400,
                                                    zIndex: 1,
                                                    backgroundColor: 'background.paper',
                                                    width: 70,
                                                    minWidth: 70,
                                                    maxWidth: 70,
                                                    px: 0,
                                                    overflow: 'visible',
                                                }
                                                : {px: 0, overflow: 'visible'}}>
                                                {showSchedule && !isSubtotal ? (
                                                    <ScheduleChip
                                                        mode={mode}
                                                        item={scheduleChipItem}
                                                        label={getScheduleName(scheduleNameMap, item)}
                                                        mustFill={isMustFillScheduleCode(
                                                            normalizeLineCode((item as any)?.code || (item as any)?.scheduleCode),
                                                            getScheduleName(scheduleNameMap, item),
                                                        )}
                                                        onClick={() => {
                                                            try {
                                                                if (typeof onScheduleClick === 'function') onScheduleClick(item);
                                                            } catch {
                                                            }
                                                        }}
                                                    />
                                                ) : null}
                                            </TableCell>
                                            {/* Client */}
                                            <TableCell align="center" id='client'>
                                                {!isSubtotal && isParent && isExpanded ? (
                                                    <Box sx={{flex: 1}}/>
                                                ) : (
                                                    <Typography variant="body2"
                                                                sx={{fontSize: 14, textAlign: 'center'}}>
                                                        {formatNumber(clientVal || 0, true)}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            {fsOpeningEditMode && (
                                                <TableCell align="center" id='fsEdit'>
                                                    {!isParent && !isSubtotal && !(isCode(item, ...FS_CODE.BS.NET_CURRENT) || isCode(item, ...FS_CODE.BS.NET_ASSETS) || isCode(item, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) && isExclusiveCode((item as any)?.code) ? (
                                                        <MoneyField
                                                            value={fsOpeningAmounts[Number(item.fsId)] || 0}
                                                            showCents
                                                            align="center"
                                                            tiny
                                                            editable
                                                            compact
                                                            sx={{
                                                                flex: 1,
                                                                fontSize: 14,
                                                                '& .MuiInputBase-input': {
                                                                    paddingLeft: '0.8px',
                                                                    paddingRight: '0.8px',
                                                                    paddingTop: '0.4px',
                                                                    paddingBottom: '0.4px',
                                                                }
                                                            }}
                                                            onFocus={() => {
                                                                // Mode 2 辅助输入：当用户点击第二个 InputField 时自动填充
                                                                if (currentMode === AdjustmentMode.ADJUST_FS_TO_FS &&
                                                                    mode2CapturedValue !== null &&
                                                                    mode2CapturedFsId !== null &&
                                                                    mode2CapturedFsId !== Number(item.fsId) &&
                                                                    !mode2AssistUsed) {

                                                                    // 获取当前 item 的 entry
                                                                    const currentEntry = (item as any)?.entry; // 'Dr' 或 'Cr'

                                                                    // 根据 WP_TO_WP 的方式计算值：entry 相同取负数，不同保持正数
                                                                    let calculatedValue = mode2CapturedValue;
                                                                    if (mode2CapturedEntry && currentEntry) {
                                                                        if (mode2CapturedEntry === currentEntry) {
                                                                            calculatedValue = -mode2CapturedValue;
                                                                        } else {
                                                                            calculatedValue = mode2CapturedValue;
                                                                        }
                                                                    } else {
                                                                        // 如果没有 entry 信息，默认取负数
                                                                        calculatedValue = -mode2CapturedValue;
                                                                    }

                                                                    // 自动填充值
                                                                    const newAmounts = {...fsOpeningAmounts};
                                                                    newAmounts[Number(item.fsId)] = calculatedValue;
                                                                    setFsOpeningAmounts(newAmounts);

                                                                    // 标记辅助已使用，清空捕获的值
                                                                    setMode2AssistUsed(true);
                                                                    setMode2CapturedValue(null, null, null);

                                                                    // 触发数据回调
                                                                    const findNodeById = (nodes: any[], targetId: number): any => {
                                                                        for (const node of nodes || []) {
                                                                            if (Number(node.fsId) === targetId) return node;
                                                                            if (Array.isArray(node.children)) {
                                                                                const found = findNodeById(node.children, targetId);
                                                                                if (found) return found;
                                                                            }
                                                                        }
                                                                        return null;
                                                                    };

                                                                    const rowData = Object.entries(newAmounts)
                                                                        .filter(([_, amount]) => Math.abs(amount) > 0.01)
                                                                        .map(([fsIdStr, amount]) => {
                                                                            const fsId = Number(fsIdStr);
                                                                            const node = findNodeById(fsTree as any[], fsId);
                                                                            if (!node) return null;

                                                                            const client = bsResolveAdjustmentValue(
                                                                                node,
                                                                                'client',
                                                                                computedClientMap,
                                                                                {
                                                                                    afterTaxFromPL: resolvedAfterTaxFromPL ?? 0,
                                                                                    afterTaxClientFromPL: resolvedAfterTaxClientFromPL ?? 0
                                                                                }
                                                                            );

                                                                            const originalAudited = bsResolveAdjustmentValue(
                                                                                node,
                                                                                'audited',
                                                                                computedAuditedMap,
                                                                                {
                                                                                    afterTaxFromPL: resolvedAfterTaxFromPL ?? 0,
                                                                                    afterTaxClientFromPL: resolvedAfterTaxClientFromPL ?? 0
                                                                                }
                                                                            );

                                                                            const adjustHis = originalAudited - client;
                                                                            const closing = client + amount + adjustHis;

                                                                            return {
                                                                                fsId: node.fsId,
                                                                                code: normalizeLineCode((node as any)?.code || (node as any)?.scheduleCode),
                                                                                scheduleName: node.scheduleName || node.name || '',
                                                                                statement: 'BS' as const,
                                                                                entry: (node as any)?.entry,
                                                                                adjustmentAmount: amount,
                                                                                openingAmount: client,
                                                                                closingAmount: closing,
                                                                                adjustHisAmount: adjustHis,
                                                                            };
                                                                        })
                                                                        .filter(d => d !== null) as any[];

                                                                    if (onFsOpeningDataChange) {
                                                                        onFsOpeningDataChange(rowData);
                                                                    }
                                                                }
                                                            }}
                                                            onChangeNumber={(val: number) => {
                                                                const newAmounts = {...fsOpeningAmounts};
                                                                newAmounts[Number(item.fsId)] = val;
                                                                setFsOpeningAmounts(newAmounts);

                                                                // Mode 2 辅助输入：捕获第一个输入的值
                                                                if (currentMode === AdjustmentMode.ADJUST_FS_TO_FS &&
                                                                    mode2CapturedValue === null &&
                                                                    !mode2AssistUsed &&
                                                                    Math.abs(val) > 0.01) {
                                                                    // 获取当前 item 的 entry
                                                                    const currentEntry = (item as any)?.entry || null;
                                                                    setMode2CapturedValue(val, Number(item.fsId), currentEntry);
                                                                }

                                                                // 收集所有非零数据并回调
                                                                // 使用扁平化的方式从 fsTree 查找节点
                                                                const findNodeById = (nodes: any[], targetId: number): any => {
                                                                    for (const node of nodes || []) {
                                                                        if (Number(node.fsId) === targetId) return node;
                                                                        if (Array.isArray(node.children)) {
                                                                            const found = findNodeById(node.children, targetId);
                                                                            if (found) return found;
                                                                        }
                                                                    }
                                                                    return null;
                                                                };

                                                                const rowData = Object.entries(newAmounts)
                                                                    .filter(([_, amount]) => Math.abs(amount) > 0.01)
                                                                    .map(([fsIdStr, amount]) => {
                                                                        const fsId = Number(fsIdStr);
                                                                        const node = findNodeById(fsTree as any[], fsId);
                                                                        if (!node) return null;

                                                                        // 计算 Client 值
                                                                        const client = bsResolveAdjustmentValue(
                                                                            node,
                                                                            'client',
                                                                            computedClientMap,
                                                                            {
                                                                                afterTaxFromPL: resolvedAfterTaxFromPL ?? 0,
                                                                                afterTaxClientFromPL: resolvedAfterTaxClientFromPL ?? 0
                                                                            }
                                                                        );

                                                                        // 计算原始 Audited 值（不含用户输入）
                                                                        const originalAudited = bsResolveAdjustmentValue(
                                                                            node,
                                                                            'audited',
                                                                            computedAuditedMap,
                                                                            {
                                                                                afterTaxFromPL: resolvedAfterTaxFromPL ?? 0,
                                                                                afterTaxClientFromPL: resolvedAfterTaxClientFromPL ?? 0
                                                                            }
                                                                        );

                                                                        // 历史调整值 = 原始 Audited - Client
                                                                        const adjustHis = originalAudited - client;

                                                                        // 新的 Audited = Client + 用户输入 + 历史调整
                                                                        const closing = client + amount + adjustHis;

                                                                        return {
                                                                            fsId: node.fsId,
                                                                            code: normalizeLineCode((node as any)?.code || (node as any)?.scheduleCode),
                                                                            scheduleName: node.scheduleName || node.name || '',
                                                                            statement: 'BS' as const,
                                                                            entry: (node as any)?.entry,
                                                                            adjustmentAmount: amount,
                                                                            openingAmount: client,
                                                                            closingAmount: closing,
                                                                            adjustHisAmount: adjustHis,
                                                                        };
                                                                    })
                                                                    .filter(d => d !== null) as any[];

                                                                if (onFsOpeningDataChange) {
                                                                    onFsOpeningDataChange(rowData);
                                                                }
                                                            }}
                                                        />
                                                    ) : null}
                                                </TableCell>
                                            )}
                                            {/* Adjust #No */}
                                            {adjustmentColumns.map((col: any) => (
                                                <TableCell align="center" id='adjust' key={`${col.key}_${item.fsId}`}>
                                                    {col.render(item, isParent, isExpanded, isSubtotal)}
                                                </TableCell>
                                            ))}
                                            <TableCell align="center" id='audit'>
                                                {!isSubtotal && isParent && isExpanded && (
                                                    !isCode(item, ...FS_CODE.BS.CAPITAL_AND_RESERVES) ||
                                                    mode === 'adjustment'
                                                ) ? (
                                                    <Box sx={{flex: 1}}/>
                                                ) : (() => {
                                                    // 在 Round 模式下叠加四舍五入调整值
                                                    let displayVal = auditedVal || 0;
                                                    const nodeCode = String((isSubtotal && parentItem ? parentItem : item)?.code || '');
                                                    const roundingAdj = isRounding ? (roundingAdjustments[nodeCode] ?? 0) : 0;
                                                    if (roundingAdj !== 0) displayVal += roundingAdj;
                                                    // 'round' 模式下显示拆分标记，'rounded' 模式下不显示
                                                    const hasRoundingMark = auditedRounded === 'round' && roundingAdj !== 0;
                                                    // PL 源的 rounding (BS-EQ-LFP) 不在 BS 显示 delete 图标，仅在 PL 侧操作
                                                    const isPlSourced = roundingSourceStatement[nodeCode] === 'PL';

                                                    if (!hasRoundingMark) {
                                                        // 无拆分标记 → 显示合并后的值
                                                        return (
                                                            <Typography variant="body2"
                                                                        sx={{fontSize: 14, textAlign: 'center'}}>
                                                                {formatNumber(displayVal, !isRounding)}
                                                            </Typography>
                                                        );
                                                    }

                                                    // 有 Round 调整 → 单行 xxx+xxx，"+xxx" 标黄 + 删除图标(仅 BS 源)
                                                    return (
                                                        <Box sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            gap: 0.5
                                                        }}>
                                                            <Typography variant="body2" component="span"
                                                                        sx={{fontSize: 14}}>
                                                                {auditedVal === 0 ? '0' : formatNumber(auditedVal, false)}
                                                            </Typography>
                                                            <Typography variant="body2" component="span"
                                                                        sx={{
                                                                            fontSize: 14,
                                                                            fontWeight: 600,
                                                                            color: 'warning.main'
                                                                        }}>
                                                                {roundingAdj > 0 ? '+' : ''}{roundingAdj}
                                                            </Typography>
                                                            {!isPlSourced && (
                                                                <IconButton size="small"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setDeleteAnchor({
                                                                                    el: e.currentTarget,
                                                                                    nodeCode
                                                                                });
                                                                            }}
                                                                            sx={{
                                                                                p: 0,
                                                                                ml: 0.25,
                                                                                color: 'text.secondary',
                                                                                '&:hover': {color: 'error.main'}
                                                                            }}>
                                                                    <CancelOutlinedIcon sx={{fontSize: 14}}/>
                                                                </IconButton>
                                                            )}
                                                        </Box>
                                                    );
                                                })()}
                                            </TableCell>
                                            {/* Last Year */}
                                            {!hideLastYearColumns && (
                                                <TableCell align="center" id='lastYear' sx={lastYearColumnSeparatorSx}>
                                                    {!isSubtotal && isParent && isExpanded ? (
                                                        <Box sx={{flex: 1}}/>
                                                    ) : (() => {
                                                        // For Last Year, directly use computed values from computedLastYearAuditedMap
                                                        // which already handles parent sums and derived nodes correctly
                                                        let lastYearVal = 0;
                                                        if (isSubtotal && parentItem) {
                                                            // For subtotal rows, use parent's computed value
                                                            lastYearVal = computedLastYearAuditedMap[Number(parentItem.fsId)] || 0;
                                                        } else if (isCode(item, ...FS_CODE.BS.NET_CURRENT) ||
                                                            isCode(item, ...FS_CODE.BS.NET_ASSETS) ||
                                                            isCode(item, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) {
                                                            // For derived nodes, use computed value directly
                                                            lastYearVal = computedLastYearAuditedMap[Number(item.fsId)] || 0;
                                                        } else if (hasChildren(item)) {
                                                            // For parent nodes, use computed sum
                                                            lastYearVal = computedLastYearAuditedMap[Number(item.fsId)] || 0;
                                                        } else {
                                                            // For leaf nodes, use lastYearAudited field directly
                                                            lastYearVal = Number((item as any)?.lastYearAudited ?? 0) || 0;
                                                        }
                                                        return (
                                                            <Typography variant="body2"
                                                                        sx={{fontSize: 14, textAlign: 'center'}}>
                                                                {formatNumber(lastYearVal, !isRounding)}
                                                            </Typography>
                                                        );
                                                    })()}
                                                </TableCell>
                                            )}
                                            {/* Change */}
                                            {!hideLastYearColumns && (
                                                <TableCell align="center" id='change'>
                                                    {!isSubtotal && isParent && isExpanded ? (
                                                        <Box sx={{flex: 1}}/>
                                                    ) : (() => {
                                                        // Change 列也需要叠加 rounding adjustment（与 Audited 列一致）
                                                        // 父节点 / 小计行的 auditedVal 已包含 sumRoundingForNode，仅叶子节点需要额外加
                                                        const changeNodeCode = String((isSubtotal && parentItem ? parentItem : item)?.code || '');
                                                        const isLeafForChange = !isSubtotal && !hasChildren(item);
                                                        const changeRoundingAdj = (isRounding && isLeafForChange) ? (roundingAdjustments[changeNodeCode] ?? 0) : 0;
                                                        const currentValue = (auditedVal || 0) + changeRoundingAdj;
                                                        // For Last Year, directly use computed values from computedLastYearAuditedMap
                                                        let lastYearValue = 0;
                                                        if (isSubtotal && parentItem) {
                                                            lastYearValue = computedLastYearAuditedMap[Number(parentItem.fsId)] || 0;
                                                        } else if (isCode(item, ...FS_CODE.BS.NET_CURRENT) ||
                                                            isCode(item, ...FS_CODE.BS.NET_ASSETS) ||
                                                            isCode(item, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) {
                                                            lastYearValue = computedLastYearAuditedMap[Number(item.fsId)] || 0;
                                                        } else if (hasChildren(item)) {
                                                            lastYearValue = computedLastYearAuditedMap[Number(item.fsId)] || 0;
                                                        } else {
                                                            lastYearValue = Number((item as any)?.lastYearAudited ?? 0) || 0;
                                                        }

                                                        if (lastYearValue === 0 || !Number.isFinite(lastYearValue)) {
                                                            return (
                                                                <Typography variant="body2" sx={{
                                                                    fontSize: 14,
                                                                    color: 'text.secondary'
                                                                }}>
                                                                    N/A
                                                                </Typography>
                                                            );
                                                        }

                                                        const changePercent = ((currentValue - lastYearValue) / lastYearValue) * 100;

                                                        if (!Number.isFinite(changePercent) || changePercent === 0) {
                                                            return (
                                                                <Typography variant="body2" sx={{
                                                                    fontSize: 14,
                                                                    color: 'text.secondary'
                                                                }}>
                                                                    N/A
                                                                </Typography>
                                                            );
                                                        }

                                                        return (
                                                            <Box
                                                                sx={isSubtotal ? {'& .MuiTypography-root': {fontWeight: 600}} : undefined}>
                                                                <UpDownIconDisplay changePercent={changePercent}/>
                                                            </Box>
                                                        );
                                                    })()}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                {/* 四舍五入差值调整弹窗 */}
                <RoundingAdjustDialog
                    open={roundingDialogOpen}
                    onClose={handleRoundingDialogClose}
                    difference={roundingDifference}
                    targetNodes={roundingTargetNodes}
                    onApply={handleRoundingAdjustApply}
                    savedNodeValues={roundingSavedNodeValues}
                    periods={roundingPlPeriods}
                />
                {/* 共享 Popover：Rounding 删除二次确认（表格层级唯一实例） */}
                <Popover
                    open={!!deleteAnchor}
                    anchorEl={deleteAnchor?.el}
                    onClose={() => setDeleteAnchor(null)}
                    anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                >
                    <Box p={2}>
                        <Typography variant="body2" sx={{mb: 1}}>
                            Confirm to remove rounding adjustment?
                        </Typography>
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button size="small" onClick={() => setDeleteAnchor(null)}>Cancel</Button>
                            <Button size="small" color="error" variant="contained"
                                    onClick={() => {
                                        handleRoundingRemove();
                                        setDeleteAnchor(null);
                                    }}>
                                Confirm
                            </Button>
                        </Stack>
                    </Box>
                </Popover>
            </Box>
        );
    }

    // FS mode rendering - 使用与 adjustment 模式相同的表格布局
    if (mode === 'fs') {
        const rows = useMemo(() => flatten((fsTree || []) as any[], 0), [fsTree, expanded]);

        // GL FS Summary（readOnly）：隐藏 Current / Last Year 均为 0 的行
        const fsSummaryVisibleByFsId = useMemo(() => {
            if (!readOnly) return null;
            const isDerivedNode = (node: any) => isCode(
                node,
                ...FS_CODE.BS.NET_CURRENT,
                ...FS_CODE.BS.NET_ASSETS,
                ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD,
                ...FS_CODE.BS.RETAINED_PROFITS_OR_LOSSES,
            );
            const isDerivedLastYearNode = (node: any) => isCode(
                node,
                ...FS_CODE.BS.NET_CURRENT,
                ...FS_CODE.BS.NET_ASSETS,
                ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD,
            );
            const resolveCurrent = (node: any): number => {
                const fsId = Number(node?.fsId);
                const isParent = Array.isArray(node?.children) && node.children.length > 0;
                const isProtectedHeader = isCode(node, ...HEADER_CODE_LIST);
                if (isCode(node, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) {
                    return glFsSummaryDisplayAmount(node?.code, resolvedAfterTaxFromPL ?? 0);
                }
                let amount = computeAllComputedAmounts[fsId] ?? 0;
                if (!isParent && !isDerivedNode(node) && !isProtectedHeader && !(fsId in computeAllComputedAmounts)) {
                    amount = parseAmountDisplay(amountDisplays[fsId] ?? '0');
                }
                if (isCode(node, ...FS_CODE.BS.RETAINED_PROFITS_OR_LOSSES)) {
                    const importedAl = parseAmountDisplay(amountDisplays[fsId] ?? '0');
                    amount = bsRetainedEarningsUseStored
                        ? importedAl
                        : (derivedRetainedEarningsFS ?? amount);
                }
                return glFsSummaryDisplayAmount(node?.code, amount);
            };
            const resolveLastYear = (node: any): number => {
                const fsId = Number(node?.fsId);
                const isParent = Array.isArray(node?.children) && node.children.length > 0;
                const isProtectedHeader = isCode(node, ...HEADER_CODE_LIST);
                if (isCode(node, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) {
                    return glFsSummaryDisplayAmount(node?.code, effectivePlLastYearFromPL ?? 0);
                }
                let amount = computeAllLastYearAmounts[fsId] ?? 0;
                if (!isParent && !isDerivedLastYearNode(node) && !isProtectedHeader && !(fsId in computeAllLastYearAmounts)) {
                    amount = parseAmountDisplay(lastYearAmounts[fsId] ?? '0');
                }
                return glFsSummaryDisplayAmount(node?.code, amount);
            };
            return buildFsSummaryVisibleByFsId(
                (fsTree || []) as any[],
                resolveCurrent,
                resolveLastYear,
                { hideLastYear: !!hideLastYearColumns },
            );
        }, [
            readOnly, fsTree, computeAllComputedAmounts, computeAllLastYearAmounts,
            amountDisplays, lastYearAmounts, resolvedAfterTaxFromPL, effectivePlLastYearFromPL,
            bsRetainedEarningsUseStored, derivedRetainedEarningsFS, hideLastYearColumns,
            glFsSummaryDisplayAmount,
        ]);

        return (
            <Box
                ref={fillRootRef}
                sx={{
                width: '100%',
                height: '100%',
                maxHeight: fillHeightPx != null ? fillHeightPx : undefined,
                minHeight: 0,
                overflow: 'hidden',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <Stack direction="row" justifyContent="flex-start" alignItems="flex-start" spacing={4} mb={0.5}>
                    <Box sx={{minWidth: 0}}>
                        {!!resolvedCompanyName && (
                            <Typography variant="h6" fontWeight={700} mb={0.5}>{resolvedCompanyName}</Typography>)}
                        <Typography variant="subtitle1" fontWeight={600} mb={0.5}>Balance Sheet items</Typography>
                        {asAtPeriodTo && (
                            <Typography variant="subtitle1" fontWeight={700} mb={1}>As
                                at {formatAsAtDate(asAtPeriodTo)}</Typography>
                        )}
                    </Box>
                    <FsMaterialityHeader
                        companyId={resolvedCompanyId}
                        annualId={annualId}
                        statement="BS"
                    />
                </Stack>
                <Divider sx={{mb: 1}}/>
                <TableContainer ref={tableContainerRef} component={Paper} sx={{flex: 1, minHeight: 0, overflow: 'auto'}}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow sx={{
                                '& .MuiTableCell-root': {
                                    ...fsCompactCellPadSx,
                                }
                            }}>
                                <TableCell sx={{
                                    width: (lastYearOnly && lastYearRemarkByFsCode
                                        && Object.keys(lastYearRemarkByFsCode).length > 0) ? '28%' : '36.5%',
                                }}/>
                                {lastYearOnly && lastYearRemarkByFsCode
                                    && Object.keys(lastYearRemarkByFsCode).length > 0 && (
                                    <TableCell sx={{width: '32%', fontWeight: 700}}>Remark</TableCell>
                                )}
                                {/* 188 = PL SINGLE Current/Last Year init (160 MoneyField + 28 COT trailing) */}
                                {!lastYearOnly && (
                                    <TableCell align="center" sx={{width: 188, fontWeight: 700}}>Current</TableCell>
                                )}
                                {!hideLastYearColumns && (
                                    <TableCell align="center" sx={{width: 188, fontWeight: 700, ...lastYearColumnSeparatorSx}}>Last
                                        Year</TableCell>
                                )}
                                {!hideLastYearColumns && !lastYearOnly && (
                                    <TableCell align="center" sx={{width: 120, fontWeight: 700}}>Change</TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && (
                                <FsStatementTableSkeletonRows
                                    columns={buildFsBsSkeletonColumns({
                                        hideLastYearColumns,
                                        lastYearOnly,
                                        showRemark: !!lastYearOnly && !!lastYearRemarkByFsCode
                                            && Object.keys(lastYearRemarkByFsCode).length > 0,
                                    })}
                                />
                            )}
                            {!loading && rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">No data</TableCell>
                                </TableRow>
                            ) : (
                                rows.map(({item, level, isParent, isSubtotal, parentItem}) => {
                                    if (fsSummaryVisibleByFsId) {
                                        const visibilityId = isSubtotal && parentItem
                                            ? Number(parentItem.fsId)
                                            : Number(item.fsId);
                                        if (fsSummaryVisibleByFsId[visibilityId] === false) return null;
                                    }
                                    const isExpanded = expanded[Number(item.fsId)];
                                    const isDerived = isCode(item, ...FS_CODE.BS.NET_CURRENT) || isCode(item, ...FS_CODE.BS.NET_ASSETS) || isCode(item, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD) || isCode(item, ...FS_CODE.BS.RETAINED_PROFITS_OR_LOSSES);
                                    const isProtectedHeader = isCode(item, ...HEADER_CODE_LIST);
                                    // BS-NCL-BLS, BS-NCL-OFL: non-editable in current year, but editable in lastYear (FS mode only)
                                    const isNonEditableCode = isCode(item, ...FS_CODE.BS.BANK_LOAN_NON_CURRENT, ...FS_CODE.BS.VEHICLE_LOAN_NON_CURRENT);
                                    const isLastYearEditableCode = isCode(item, ...FS_CODE.BS.BANK_LOAN_NON_CURRENT, ...FS_CODE.BS.VEHICLE_LOAN_NON_CURRENT);
                                    const isNonEditable = isNonEditableCode;
                                    const isLastYearEditable = mode === 'fs' && isLastYearEditableCode;

                                    let editableLeaf = !readOnly && !isParent && !isDerived && !isProtectedHeader && !isNonEditable && !isSubtotal;
                                    // BS-EQ-AL: derived (non-editable) in Current column, but editable in Last Year column
                                    // BS-NCL-BLS, BS-NCL-OFL: also editable in Last Year column (FS mode only)
                                    const isDerivedLastYear = isCode(item, ...FS_CODE.BS.NET_CURRENT) || isCode(item, ...FS_CODE.BS.NET_ASSETS) || isCode(item, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD);
                                    const isNonEditableLastYear = isNonEditable && !isLastYearEditable;
                                    let editableLeafLastYear = !readOnly && !isParent && !isDerivedLastYear && !isProtectedHeader && !isNonEditableLastYear && !isSubtotal;
                                    let computedAmount = computeAllComputedAmounts[item.fsId] ?? 0;

                                    // readOnly 模式下，叶子节点不在 computeAllComputedAmounts 中，需要从 amountDisplays 取值
                                    if (!isParent && !isDerived && !isProtectedHeader && !(item.fsId in computeAllComputedAmounts)) {
                                        computedAmount = parseAmountDisplay(amountDisplays[item.fsId] ?? '0');
                                    }

                                    // 如果是小计行，使用父节点的计算值
                                    if (isSubtotal && parentItem) {
                                        computedAmount = computeAllComputedAmounts[parentItem.fsId] ?? 0;
                                    }

                                    if (isCode(item, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) {
                                        editableLeaf = false;
                                        computedAmount = resolvedAfterTaxFromPL ?? 0;
                                    }

                                    const displayAmount = glFsSummaryDisplayAmount(
                                        item.code,
                                        computedAmount,
                                        isSubtotal ? parentItem?.code : undefined,
                                    );

                                    const displayName = isSubtotal ? '' : getDisplayName(item);
                                    const shouldShowValue = !isParent || !isExpanded;
                                    const lineFsCode = String(item?.code || '').trim();
                                    const nameClickable = !!readOnly
                                        && !!onFsLineClick
                                        && !isParent
                                        && !isSubtotal
                                        && !!lineFsCode
                                        && (!navigableFsCodes || navigableFsCodes.has(lineFsCode));
                                    const showRemarkCol = !!lastYearOnly
                                        && !!lastYearRemarkByFsCode
                                        && Object.keys(lastYearRemarkByFsCode).length > 0;
                                    const lastYearRemark = (!isSubtotal && lineFsCode && lastYearRemarkByFsCode)
                                        ? (lastYearRemarkByFsCode[lineFsCode] || '')
                                        : '';

                                    return (
                                        <TableRow key={item.fsId} hover={!isSubtotal} sx={{
                                            '& .MuiTableCell-root': {
                                                ...fsCompactCellPadSx,
                                                ...((item as any).level === 2 && !isParent && !isSubtotal && {
                                                    paddingTop: FS_LEVEL2_CELL_PY,
                                                    paddingBottom: FS_LEVEL2_CELL_PY,
                                                }),
                                                ...(isSubtotal && {
                                                    borderBottom: '2px solid',
                                                    borderBottomColor: 'divider',
                                                    fontWeight: 600
                                                })
                                            }
                                        }}>
                                            <TableCell>
                                                <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                    <Box sx={{pl: level * 2}}/>
                                                    {isParent && !isSubtotal ? (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => toggleNode(Number(item.fsId))}
                                                            sx={fsCompactIconButtonSx}
                                                        >
                                                            {isExpanded ? <ExpandLessIcon fontSize="small"/> :
                                                                <ExpandMoreIcon fontSize="small"/>}
                                                        </IconButton>
                                                    ) : (
                                                        <Box sx={{width: 15}}/>
                                                    )}
                                                    <Typography
                                                        variant="body2"
                                                        onClick={nameClickable
                                                            ? () => onFsLineClick!(lineFsCode)
                                                            : undefined}
                                                        sx={{
                                                            mr: 1,
                                                            ...fsCompactNameSx,
                                                            ...((isSubtotal || (item as any).level === 2) && {fontWeight: 600}),
                                                            ...((item as any).level === 2 && {fontFamily: '"Times New Roman", serif'}),
                                                            ...(nameClickable && {
                                                                cursor: 'pointer',
                                                                color: 'primary.main',
                                                                '&:hover': {textDecoration: 'underline'},
                                                            }),
                                                        }}
                                                    >
                                                        {displayName}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            {showRemarkCol && (
                                                <TableCell sx={{verticalAlign: 'middle'}}>
                                                    {lastYearRemark ? (
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            title={lastYearRemark}
                                                            sx={{
                                                                display: 'block',
                                                                fontSize: 12,
                                                                lineHeight: 1.35,
                                                                whiteSpace: 'normal',
                                                                wordBreak: 'break-word',
                                                            }}
                                                        >
                                                            {lastYearRemark}
                                                        </Typography>
                                                    ) : null}
                                                </TableCell>
                                            )}
                                            {!lastYearOnly && (
                                            <TableCell align="center">
                                                <Box sx={fsAmountCellContentSx}>
                                                {(() => {
                                                    const isRetainedProfits = isCode(item, ...FS_CODE.BS.RETAINED_PROFITS_OR_LOSSES);
                                                    const importedValue = parseAmountDisplay(amountDisplays[item.fsId] ?? '0');

                                                    if (editableLeaf) {
                                                        return (
                                                            <MoneyField
                                                                value={importedValue}
                                                                showCents
                                                                align="center"
                                                                tiny
                                                                compact
                                                                editable
                                                                onChangeNumber={(num) => {
                                                                    updateFsImportedAmount(Number(item.fsId), num, item);
                                                                }}
                                                                sx={fsCompactMoneyFieldSx}
                                                            />
                                                        );
                                                    }

                                                    if (isRetainedProfits && !readOnly && !isSubtotal) {
                                                        // DIR-style Final split: null/0 init → normal; Edit → override (incl. 0); Cancel → clear to derived.
                                                        const splitActive = bsRetainedEarningsUseStored;
                                                        return (
                                                            <MoneyField
                                                                value={derivedRetainedEarningsFS ?? 0}
                                                                suffixValue={splitActive ? importedValue : 0}
                                                                separator=">"
                                                                icon={<EditIcon fontSize="small"/>}
                                                                cancelIcon={<CloseIcon fontSize="small"/>}
                                                                forceShowSplitMode={splitActive}
                                                                onEditStart={() => handleRetainedProfitsEditStart(
                                                                    Number(item.fsId),
                                                                    item,
                                                                    derivedRetainedEarningsFS,
                                                                )}
                                                                onSuffixChange={(num) => handleRetainedProfitsSuffixChange(
                                                                    Number(item.fsId),
                                                                    num,
                                                                    item,
                                                                )}
                                                                onEditCancel={() => handleRetainedProfitsEditCancel(Number(item.fsId), item)}
                                                                showCents
                                                                align="center"
                                                                tiny
                                                                compact
                                                                sx={fsCompactMoneyFieldSx}
                                                            />
                                                        );
                                                    }

                                                    if (!(shouldShowValue || isSubtotal)) {
                                                        return <Box sx={{width: '100%'}}/>;
                                                    }

                                                    return (
                                                        <MoneyField
                                                            value={displayAmount || 0}
                                                            showCents
                                                            align="center"
                                                            tiny
                                                            compact
                                                            sx={{
                                                                ...fsCompactMoneyFieldSx,
                                                                ...(isSubtotal && {fontWeight: 600}),
                                                            }}
                                                        />
                                                    );
                                                })()}
                                                </Box>
                                            </TableCell>
                                            )}
                                            {!hideLastYearColumns && (
                                                <>
                                                    <TableCell align="center" sx={lastYearColumnSeparatorSx}>
                                                        <Box sx={fsAmountCellContentSx}>
                                                        {editableLeafLastYear ? (
                                                            <MoneyField
                                                                value={parseAmountDisplay(lastYearAmounts[item.fsId] ?? '0')}
                                                                showCents
                                                                align="center"
                                                                tiny
                                                                compact
                                                                editable
                                                                onChangeNumber={(num) => {
                                                                    let nextNum = num;
                                                                    // Dividend must be negative
                                                                    if (isCode(item, ...FS_CODE.BS.DIVIDEND) && nextNum > 0) {
                                                                        nextNum = -nextNum;
                                                                    }
                                                                    setLastYearAmounts(prev => ({
                                                                        ...prev,
                                                                        [item.fsId]: String(nextNum)
                                                                    }));
                                                                }}
                                                                sx={fsCompactMoneyFieldSx}
                                                            />
                                                        ) : (shouldShowValue || isSubtotal) ? (
                                                            <MoneyField
                                                                value={(() => {
                                                                    // Special handling for Profit/Loss for the year
                                                                    if (isCode(item, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) {
                                                                        return effectivePlLastYearFromPL;
                                                                    }

                                                                    let computedLastYear = computeAllLastYearAmounts[item.fsId] ?? 0;
                                                                    // readOnly 模式下，叶子节点不在 computeAllLastYearAmounts 中，从 lastYearAmounts 取值
                                                                    if (!isParent && !isDerivedLastYear && !isProtectedHeader && !(item.fsId in computeAllLastYearAmounts)) {
                                                                        computedLastYear = parseAmountDisplay(lastYearAmounts[item.fsId] ?? '0');
                                                                    }
                                                                    if (isSubtotal && parentItem) {
                                                                        computedLastYear = computeAllLastYearAmounts[parentItem.fsId] ?? 0;
                                                                    }
                                                                    const displayLastYear = glFsSummaryDisplayAmount(
                                                                        item.code,
                                                                        computedLastYear,
                                                                        isSubtotal ? parentItem?.code : undefined,
                                                                    );
                                                                    return displayLastYear;
                                                                })()}
                                                                showCents
                                                                align="center"
                                                                compact
                                                                tiny
                                                                sx={{
                                                                    ...fsCompactMoneyFieldSx,
                                                                    ...(isSubtotal && {fontWeight: 600}),
                                                                }}
                                                            />
                                                        ) : (
                                                            <Box sx={{width: '100%'}}/>
                                                        )}
                                                        </Box>
                                                    </TableCell>
                                                    {!lastYearOnly && (
                                                    <TableCell align="center">
                                                        {(() => {
                                                            const isRetainedProfitsRow = isCode(item, ...FS_CODE.BS.RETAINED_PROFITS_OR_LOSSES);
                                                            let currentValue = computedAmount || 0;
                                                            if (editableLeaf) {
                                                                currentValue = parseAmountDisplay(amountDisplays[item.fsId] ?? '0');
                                                            } else if (isRetainedProfitsRow) {
                                                                const importedAl = parseAmountDisplay(amountDisplays[item.fsId] ?? '0');
                                                                currentValue = bsRetainedEarningsUseStored
                                                                    ? importedAl
                                                                    : (derivedRetainedEarningsFS ?? computedAmount);
                                                            }
                                                            if (readOnly) {
                                                                currentValue = glFsSummaryDisplayAmount(
                                                                    item.code,
                                                                    currentValue,
                                                                    isSubtotal ? parentItem?.code : undefined,
                                                                );
                                                            }

                                                            // Special handling for Profit/Loss for the year
                                                            let lastYearValue;
                                                            if (isCode(item, ...FS_CODE.BS.PROFIT_LOSS_FOR_YEAR_PERIOD)) {
                                                                lastYearValue = effectivePlLastYearFromPL;
                                                            } else {
                                                                let computedLastYear = computeAllLastYearAmounts[item.fsId] ?? 0;
                                                                // readOnly 模式下，叶子节点不在 computeAllLastYearAmounts 中，从 lastYearAmounts 取值
                                                                if (!isParent && !isDerivedLastYear && !isProtectedHeader && !(item.fsId in computeAllLastYearAmounts)) {
                                                                    computedLastYear = parseAmountDisplay(lastYearAmounts[item.fsId] ?? '0');
                                                                }
                                                                if (isSubtotal && parentItem) {
                                                                    computedLastYear = computeAllLastYearAmounts[parentItem.fsId] ?? 0;
                                                                }
                                                                lastYearValue = editableLeafLastYear
                                                                    ? parseAmountDisplay(lastYearAmounts[item.fsId] ?? '0')
                                                                    : computedLastYear;
                                                            }

                                                            if (readOnly) {
                                                                lastYearValue = glFsSummaryDisplayAmount(
                                                                    item.code,
                                                                    lastYearValue,
                                                                    isSubtotal ? parentItem?.code : undefined,
                                                                );
                                                            }

                                                            if (!shouldShowValue && !isSubtotal) {
                                                                return <Box sx={{flex: 1}}/>;
                                                            }

                                                            if (lastYearValue === 0 || !Number.isFinite(lastYearValue)) {
                                                                return (
                                                                    <Typography variant="body2" sx={{
                                                                        fontSize: 14,
                                                                        color: 'text.secondary'
                                                                    }}>
                                                                        N/A
                                                                    </Typography>
                                                                );
                                                            }

                                                            const changePercent = ((currentValue - lastYearValue) / lastYearValue) * 100;

                                                            if (!Number.isFinite(changePercent) || changePercent === 0) {
                                                                return (
                                                                    <Typography variant="body2" sx={{
                                                                        fontSize: 14,
                                                                        color: 'text.secondary'
                                                                    }}>
                                                                        N/A
                                                                    </Typography>
                                                                );
                                                            }

                                                            return (
                                                                <Box
                                                                    sx={isSubtotal ? {'& .MuiTypography-root': {fontWeight: 600}} : undefined}>
                                                                    <UpDownIconDisplay changePercent={changePercent}/>
                                                                </Box>
                                                            );
                                                        })()}
                                                    </TableCell>
                                                    )}
                                                </>
                                            )}
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    }

    // Helpers (adjustment): sum property across leaf descendants
    function sumFinalOrAmount(node: any, prop: 'amount' | 'finalAmount'): number {
        if (!hasChildren(node)) {
            return Number(node?.[prop] ?? 0) || 0;
        }
        let s = 0;
        for (const ch of node.children || []) s += sumFinalOrAmount(ch, prop);
        return s;
    }
});

export type {CompanyAnnualSelectItem};
